import { pendingCue, playCue } from '../story/playback';
// バトル画面：縦長の盤面（Canvas）＋HUD（本陣HP・行動順・星条件・陣地ゲージ・情報カード・操作）。
// 進行は battle/controller.ts。サーバーとは start（setup を受け取る）と finish（commands を送る）だけ。
import { battleTutor } from '../tutorial';
import { coaching } from '../tutorial/coach';
import { BATTLE_CONFIG, BUILDING_TYPES, LATE_BUILDING_TYPES, CHARS, CHAR_IDS, ENEMIES, SHIKIGAMI, kaseiInfo, shikiFormName, placeOfStage, provinceLabel, slotLabel, strongholdLabel, statusInfo, styleOf, surgeRules, PROLOGUE_STAGE, prologueLoadout, RULES_VERSION, type BattleSetup, type CharId, type TargetRef, type StageInfo, type Command, farmBattleMs, farmGraceMs, guestStageOpen, stageStaminaCost } from '@hyakki/core';
import { companionKitText } from '../ui/companion';
import { api, ApiError, track } from '../api';
import { GUEST_CHAPTER_MSG, guestBlocked, guestLockHtml } from '../ui/google-link';
import { bgm, jingle, sfx, prepareBgm, type SfxKey } from '../audio';
import { WORLD, BUILDING_INFO, BUILDING_SPRITES, enemyBuildingRef, ROLE_STYLE, awakeStyle, battleItemArt, enemyIconHtml, shikiArtHtml, faceStyle, heroStyle, leaderFaceStyle, loadSheet, loadSheets, ougiCutinSrc, spriteStyle, stageSheets } from '../assets';
import { icon } from '../ui/icons';
import { confetti } from '../ui/confetti';
import { battleApi, localLoadout, stageById } from '../core-bridge';
import { BUILD_WHY, refCell, surgeText, type FxLook, type SurgeView, type VBoard, type VUnit } from '../battle/adapter';
import { BattleController, clearSaved, loadSaved, type SavedBattle, type SurgeEvent } from '../battle/controller';
import { demoBoard } from '../battle/demo';
import { bindBoardInput } from '../battle/input';
import { BoardView, STATUS_MARK } from '../battle/render';
import { go, parse, register } from '../router';
import { hasEnt, patch, refreshMe, setSetting, state } from '../state';
import { esc, fmt, onAct, sleep } from '../ui/dom';
import { confirmDialog, openModal, toast } from '../ui/overlay';
import { openHelp } from './help';
import { errorText, loadingHtml } from '../ui/status';
import { openHyoroShort } from '../ui/stamina-info';
import { setResult } from './result-store';
import { mountSkit } from '../ui/sweep-fx';
import { autoRun, autoRunLabel, nextAutoStage, onAutoRun, recordAutoBattle, recordSettledRuns, setStopAfter, stopAutoRun } from '../autorun';
import { designWidth, rem } from '../ui/viewport';

const SPEEDS = [1, 2, 4] as const;
/** 選べる一番速い速度：×4 は倍速パックの権利（speed4）、×2 は Google 連携、ゲストは等速 */
const maxSpeed = (): (typeof SPEEDS)[number] => (hasEnt('speed4') ? 4 : state.me?.user.guest ? 1 : 2);
/** 今の戦闘速度（端末に速い設定が残っていても、使えるところまで） */
const battleSpeedNow = () => Math.min(state.settings.battleSpeed, maxSpeed()) as (typeof SPEEDS)[number];
/** ×2から押したときに錠つきの×4を見せている（倍速パックの案内。速度は×2のまま、もう一度押すと×1） */
let speed4Tease = false;
/** ゲスト（×1だけ）か、錠つきの×4を見せているときは速度のボタンに錠 */
const speedLockHtml = () => (maxSpeed() > 1 && !speed4Tease ? '' : `<em class="guest-lock" aria-label="Google連携で×2、倍速パックで×4">${icon('lock')}</em>`);

/**
 * 出陣の操作ID。応答が届かずに再試行・再読み込みしても同じIDを送るので、サーバーは同じ戦闘を返す（兵糧は1回分）。
 * 同じ出陣先のあいだだけ使い回し（10分まで）、出陣できたら消す
 */
const START_OP_KEY = 'hyakki.startOp';
/** 「このバトルで終わる」ボタンの見た目（入っていれば「周回を続ける」） */
function stopBtnState(b: HTMLElement | null, on?: boolean) {
  if (!b) return;
  b.classList.toggle('on', !!on);
  b.setAttribute('aria-pressed', String(!!on));
  const t = b.querySelector('span');
  if (t) t.textContent = on ? '周回を\u200b続ける' : 'このバトルで\u200b終わる';
}
function startOpId(key: string): string {
  try {
    const o = JSON.parse(sessionStorage.getItem(START_OP_KEY) ?? 'null') as { key: string; id: string; at: number } | null;
    if (o && o.key === key && Date.now() - o.at < 10 * 60_000) return o.id;
  } catch {
    /* 保存できない環境では毎回新しいID */
  }
  const id = crypto.randomUUID();
  try {
    sessionStorage.setItem(START_OP_KEY, JSON.stringify({ key, id, at: Date.now() }));
  } catch {
    /* 同上 */
  }
  return id;
}
function clearStartOp() {
  try {
    sessionStorage.removeItem(START_OP_KEY);
  } catch {
    /* 同上 */
  }
}

/** プロローグの戦場の公開情報（STAGES には入れない） */
const PROLOGUE_INFO: StageInfo = {
  id: PROLOGUE_STAGE,
  chapter: 1,
  index: 7,
  name: '人の世の最後の城',
  enemyIds: ['h01', 'h09', 'h00'], // h09＝番人の侍大将、h00＝操作する酒呑童子の絵
  bossId: 'h09',
  isChapterBoss: false,
  terrain: 'capital',
  enemyLevel: 1,
};

register('battle', {
  title: 'バトル',
  full: true,
  nav: 'stages',
  async mount(el, params) {
    const bapi = battleApi();
    let saved: SavedBattle | null = null;
    const resume = params.get('resume') === '1';
    if (resume) {
      saved = loadSaved();
      if (!saved) {
        // 退避していた周回の1戦は、開き直したときにサーバーで精算済み（TOP の案内から成果を見る）
        if (params.get('autorun') === '1') return void go('top', {}, true);
        toast('中断中の戦闘が見つかりませんでした', 'error');
        go('stages', {}, true);
        return;
      }
    }
    // 周回の1戦の URL のまま開き直した（アプリが落ちて再読み込み等）：周回は終わっているので、同じ戦場を最初から始め直さない
    if (!resume && params.get('autorun') === '1' && !autoRun()) {
      go('top', {}, true);
      return;
    }
    // プロローグ（三百年前・天下統一の最終戦）：画面だけで遊ぶ酒呑童子の戦い（サーバーを通さず、報酬なし）
    const prologue = params.get('prologue') === '1';
    const stageId = prologue ? PROLOGUE_STAGE : (saved?.stageId ?? Number(params.get('stage') ?? 1));
    const charId = prologue ? ('suzune' as CharId) : ((saved?.charId ?? (params.get('char') as CharId | null) ?? state.oshi) as CharId);
    const difficulty = prologue ? 0 : (saved?.setup.difficulty ?? Number(params.get('diff') ?? 0));
    const stage = prologue ? PROLOGUE_INFO : stageById(stageId);
    if (prologue) {
      track('prologue.battle');
      prepareBgm('story_warm'); // 落城のあと、ジングルなしですぐ宴の場面の曲へ
    }
    else if (stageId === 1) track('stage1.start');
    if (!stage) {
      go('stages', {}, true);
      return;
    }
    // ゲストは第1章まで（2章からは連携の案内を出して地図へ。再開の戦闘はそのまま続けられる）
    if (!prologue && !resume && !guestStageOpen(stageId) && state.me?.user.guest) {
      go('stages', { stage: stageId }, true);
      queueMicrotask(() => guestBlocked(GUEST_CHAPTER_MSG, 'chapter'));
      return;
    }
    if (!prologue && !resume && params.get('autorun') !== '1' && difficulty === 0) {
      const cue = pendingCue(stageId, 'before');
      if (cue) { playCue(cue, 'battle', { stage: stageId, char: charId }); return; }
    }
    // オート周回で「物語の場面を見る」：前の1戦で初めて勝った戦場の後の場面、この戦場の前の場面を見てから続ける（周回は止めない）
    {
      const r = params.get('autorun') === '1' ? autoRun() : null;
      if (!prologue && !resume && r?.cfg.story) {
        const cue = (r.lastWin != null ? pendingCue(r.lastWin, 'after') : undefined) ?? (difficulty === 0 ? pendingCue(stageId, 'before') : undefined);
        if (cue) { playCue(cue, 'battle', { stage: stageId, char: charId, diff: difficulty, autorun: 1 }); return; }
      }
    }
    // 曲：章の大将＝大将戦、本城＝城攻め（和風ロック）、後半の章・防衛戦＝合戦2、それ以外＝合戦
    void bgm(prologue || stage.isChapterBoss ? 'boss2' : placeOfStage(stage.id)?.castle ? 'boss' : stage.chapter >= 6 || stage.mode === 'defense' ? 'battle2' : 'battle');
    // オート周回中の1戦か（保存してTOPへ戻っていた周回は、その1戦から続ける）
    const parked = !!(saved && autoRun()?.parked);
    const ar = (!saved || parked) && params.get('autorun') === '1' && !!autoRun();
    if (autoRun()?.parked) {
      // 別の戦闘を始めた：退避していた周回はここで終わり
      if (!ar) stopAutoRun('停止しました');
      else autoRun()!.parked = false;
    }
    // 節電モード：盤面を描かず演出も省く（1戦の時間は手番数×秒÷倍速で同じ。GAME_DESIGN 20章）。途中で盤面に切り替えられる
    let fast = ar && !!autoRun()?.cfg.fast;
    // 節電で始めた1戦（サーバーが1戦の時間を待たせる）。途中で盤面に切り替えたら待ちは外れる（以降に節電へ戻しても待たない）
    let mustWait = fast;
    let leavingForNext = false; // 周回の次の戦闘へ移るときは cleanup で周回を止めない
    // この画面にいるか（el は画面の入れ物で使い回されるので isConnected では離れたことが分からない）
    let alive = true;
    // 節電：決着してから戦果を送るまでの待ちの間（「このバトルで終わる」は予約の切り替えだけにして、勝った1戦は送る）
    let finalWait = false;

    el.innerHTML = shellHtml(placeOfStage(stage.id)?.field ?? stage.name, stage);
    if (ar) el.querySelector('.battle')!.classList.add('autorun');
    // 節電モードの表示（周回の進み・獲得・今の状態だけ）
    let ps = fast ? el.querySelector('.battle')!.appendChild(Object.assign(document.createElement('div'), { className: 'ps-panel' })) : null;
    let psState = '戦闘中';
    const drawPs = () => {
      const r = autoRun();
      if (!ps || !r) return;
      const g = r.gains;
      const top = g.items.filter((x) => x.rarity === 'ssr').length;
      // 枠と「周回をやめる」は1度だけ作り、あとは文字だけ書き換える（押している途中でボタンが作り直されると押せないため）
      if (!ps.firstElementChild)
        ps.innerHTML = `<div class="ps-box"><p class="ps-tag">${icon('auto')}節電モード</p>
          <p class="ps-run"></p><p class="ps-state"></p><ul class="ps-gains"></ul>
          <button class="btn danger ps-stop" data-act="arstop">${icon('stop')}<span>このバトルで終わる</span></button>
          <div class="btn-row"><button class="btn" data-act="psview">盤面を見る</button><button class="btn" data-act="manual">手動で戦う</button><button class="btn" data-act="park">保存してTOPへ</button></div>
          <p class="ps-grace">${icon('clock')}アプリを閉じても${farmGraceMs() / 60000}分ぶんは周回が進みます</p></div>`;
      ps.querySelector('.ps-run')!.textContent = autoRunLabel(r);
      stopBtnState(ps.querySelector('[data-act="arstop"]'), r.stopAfter);
      ps.querySelector('.ps-state')!.textContent = psState;
      ps.querySelector('.ps-gains')!.innerHTML = `<li>主EXP<b>+${fmt(g.exp)}</b></li><li>銭<b>+${fmt(g.zeni)}</b></li><li>装備<b>${fmt(g.items.length)}</b>${top ? `<em class="rar ssr">極</em><em>${top}</em>` : ''}</li>
          <li>兵糧<b>${fmt((state.stamina?.value ?? 0) + (state.stamina?.reserve ?? 0))}</b></li>`;
    };
    const setPs = (t: string) => {
      psState = t;
      drawPs();
    };
    if (ps) {
      el.querySelector('.battle')!.classList.add('powersave');
      drawPs();
    }
    // 節電モードの寸劇（設定でオフにできる。端末で動きを減らす設定なら出さない）
    let skit: ReturnType<typeof mountSkit> | null = null;
    let skitTurn = 0;
    /** 節電の札に寸劇を置く（途中で節電に切り替えたときも。札が描き直されたら置き直す） */
    const ensureSkit = (turnNow = 0) => {
      const box = ps?.querySelector<HTMLElement>('.ps-box');
      if (!box || state.settings.psSkit === false || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (skit && box.contains(box.querySelector('.swfx.skit'))) return;
      skit?.destroy();
      skit = mountSkit(box, { charId, enemyIds: stage.enemyIds, terrain: stage.terrain });
      skitTurn = turnNow;
    };
    ensureSkit();
    // プロローグは演出を見せたいので、速度は設定によらず×1（速度の切り替えも出さない）
    fixedSpeed = prologue;
    speed4Tease = false;
    el.querySelector('.battle')!.classList.toggle('fixed-speed', prologue);
    const view = new BoardView(el.querySelector('canvas')!, el.querySelector('.floaters')!);
    applyViewSettings(view);
    let raf = 0;
    let frameNo = 0;
    const loop = (t: number) => {
      // 節電モードは描かない。高速モード中は描画を間引く（4フレームに1回）
      if (!fast && (!ctlRef.turbo || frameNo++ % 4 === 0)) view.frame(t);
      raf = requestAnimationFrame(loop);
    };
    const ctlRef: { turbo: boolean } = { turbo: false };
    raf = requestAnimationFrame(loop);
    // 盤は画面全体の Canvas に描き、HUD（上・下・PCの左右の欄）を除いた空きに置く
    // 決着したら盤の大きさは固定（下の操作欄が空になって高さが変わり、勝利の瞬間に盤が拡大縮小して見えるのを防ぐ）。画面の回転・サイズ変更だけは追う
    let lastW = 0;
    let boardFrozen = false;
    const layout = () => {
      const w = window.innerWidth;
      if (boardFrozen && w === lastW) return;
      lastW = w;
      layoutBoard(el, view);
    };
    const ro = new ResizeObserver(layout);
    for (const q of ['.battle', '.b-top', '.b-bottom', '.b-side.left', '.b-side.right']) ro.observe(el.querySelector(q)!);
    layout();
    const cleanups: (() => void)[] = [() => cancelAnimationFrame(raf), () => ro.disconnect()];
    cleanups.push(() => skit?.destroy());
    const cleanup = () => cleanups.forEach((f) => f());

    // ---------- 戦闘ロジック未接続：盤面の見た目だけ表示 ----------
    if (!bapi) {
      const board = demoBoard(stage, charId);
      view.sync(board);
      renderHud(el, board, null, view);
      el.querySelector('.actions')!.innerHTML = `<div class="notready-inline"><b>戦闘ロジック準備中</b><span>盤面の表示確認用です（操作はできません）</span><button class="btn" data-act="leave">戻る</button></div>`;
      cleanups.push(onAct(el, { leave: () => go('stages') }));
      cleanups.push(
        bindBoardInput(view, {
          dragStart: () => null,
          dragStep: () => null,
          dragEnd: () => {},
          tap: (cell) => showInfo(el, board, cell, view),
          key: () => false,
        }),
      );
      return cleanup;
    }

    // 敵・建物の絵はバトルに入るときに読む（起動時には読まない）
    const sheets = preloadStage(stage);

    // ---------- 開始（サーバーから setup を受け取る。プロローグは端末だけ） ----------
    // 途中で閉じていれば（resume=1 で退避が残っている）そこから。それ以外は最初から
    if (prologue && !(resume && saved?.setup.stageId === PROLOGUE_STAGE)) saved = { v: 1, sessionId: null, setup: { stageId: PROLOGUE_STAGE, difficulty: 0, loadout: prologueLoadout(), seed: 300, rules: RULES_VERSION }, commands: [], stageId, charId, savedAt: Date.now(), local: true };
    if (!saved) {
      const overlay = el.querySelector<HTMLElement>('.board-msg')!;
      overlay.innerHTML = loadingHtml('出陣の支度中…');
      overlay.hidden = false;
      try {
        const r = await api.battleStart(stageId, difficulty, charId, ar, startOpId(`${stageId}:${difficulty}:${charId}:${ar ? 1 : 0}`), ar && !fast);
        clearStartOp();
        if (r.stamina) patch({ stamina: r.stamina }); // 応答は StaminaView（数値ではない）
        void refreshMe().catch(() => {});
        saved = { v: 1, sessionId: r.sessionId, setup: r.setup, commands: [], stageId, charId, savedAt: Date.now() };
      } catch (e) {
        if (ar) stopAutoRun(`出撃できませんでした（${errorText(e)}）`);
        // 兵糧不足（結果画面の「もう一度」など）：待つ時間と蔵出し・月パスの小窓も出す
        else if (e instanceof ApiError && e.code === 'insufficient_hyoro') openHyoroShort(stageStaminaCost(stageId, difficulty));
        if (e instanceof ApiError && e.code === 'active_session') {
          // 別の戦闘が進行中（再読み込み・別端末）。手元に保存があれば再開、無ければ放棄して出撃し直す
          const sid = (e.body as { sessionId?: string } | null)?.sessionId ?? null;
          const local = loadSaved();
          // 手元に保存が無い戦闘は続きから再開できない（別の端末・保存前に閉じた等）：聞かずに放棄して出撃し直す
          if (sid && !(local && local.sessionId === sid) && !ar) {
            // 戦果を送れずに残っていた戦闘（通信断・送信中に閉じた）なら、放棄する前に送り直す（勝った報酬を捨てない）
            let resent = false;
            try {
              const pend = JSON.parse(localStorage.getItem('hk.finish') ?? 'null') as { sessionId: string; commands: Command[] } | null;
              if (pend?.sessionId === sid) {
                await api.battleFinish(sid, pend.commands);
                localStorage.removeItem('hk.finish');
                await refreshMe().catch(() => {});
                toast('前の戦闘の戦果を受け取りました', 'good');
                resent = true;
              }
            } catch {
              /* 送れなければ放棄して出撃し直す */
            }
            if (!resent) await api.battleAbandon(sid).catch(() => {});
            clearSaved();
            patch({ suspended: null });
            go('battle', { stage: stageId, char: charId, diff: difficulty }, true);
            return cleanup;
          }
          overlay.innerHTML = `<div class="state-box"><p>進行中の戦闘があります。</p><div class="btn-row">
            <button class="btn" data-act="back">戻る</button>
            ${local && local.sessionId === sid ? '<button class="btn primary" data-act="resume">その戦闘に戻る</button>' : ''}
            <button class="btn danger" data-act="abandon">放棄して出撃</button></div></div>`;
          cleanups.push(
            onAct(overlay, {
              back: () => go('stages'),
              resume: () => go('battle', { resume: 1 }, true),
              abandon: async () => {
                if (sid) await api.battleAbandon(sid).catch(() => {});
                clearSaved();
                patch({ suspended: null });
                go('battle', { stage: stageId, char: charId, diff: difficulty }, true);
              },
            }),
          );
          return cleanup;
        }
        if (import.meta.env.DEV && e instanceof ApiError && (e.notReady || e.offline || e.status >= 500)) {
          // 開発用：サーバー無しで端末だけの試合（報酬なし）
          const setup: BattleSetup = { stageId, difficulty, loadout: localLoadout(state.profile, charId), seed: (Math.random() * 2 ** 32) >>> 0 };
          saved = { v: 1, sessionId: null, setup, commands: [], stageId, charId, savedAt: Date.now(), local: true };
          toast('サーバー未接続のため、報酬なしの試合です（開発用）', 'info', 3500);
        } else {
          overlay.innerHTML = `<div class="state-box error"><p>${esc(errorText(e))}</p><div class="btn-row"><button class="btn" data-act="back">戻る</button><button class="btn primary" data-act="retry">再試行</button></div></div>`;
          cleanups.push(
            onAct(overlay, {
              back: () => go('stages'),
              retry: () => go('battle', { stage: stageId, char: charId, diff: difficulty }, true),
            }),
          );
          return cleanup;
        }
      }
      overlay.hidden = true;
      overlay.innerHTML = '';
    }
    await sheets;
    // 妖力 参の覚醒の姿は待たずに裏で読む（読めるまでは通常の絵＋強いオーラ）
    void loadSheet('heroes_awake');
    if (CHARS[charId]) void loadSheet(`awake_${charId}`);

    const save = saved!;
    // オート周回の1戦を周回の外で続ける（周回は止まっている）：自分で決着をつけるので、節電の1戦の時間を待たない
    if (resume && !ar && save.sessionId && !save.local) void api.battleLive(save.sessionId).catch(() => {});
    // 妖力の奥義を覚えていれば、奥義のエフェクトとカットインの絵も裏で読む
    if (CHARS[charId] && save.setup.loadout.flags?.includes('surge:ougi')) {
      const look = save.setup.stageId === PROLOGUE_STAGE ? 'shuten' : charId;
      void loadSheet(`ougi_${look}`);
      preloadCutin(look);
    }
    let ctl: BattleController;
    let finished = false;
    const hooks = {
      changed: () => {
        if (ctl.over) boardFrozen = true;
        renderHud(el, ctl.board, ctl, view);
      },
      dice: (value: number, quick: boolean, three?: { dice: number[]; role?: string; perks?: string[]; enemy?: boolean }) => {
        el.querySelector('.dice-pop')!.classList.toggle('watch', ctl.auto);
        return three ? showDice3(el, value, three, quick || ctl.auto, view.speed) : showDice(el, value, quick || ctl.auto, view.speed);
      },
      skillDice: (dice: number[], mult: number, zorome: boolean, quick: boolean) => {
        el.querySelector('.dice-pop')!.classList.toggle('watch', ctl.auto);
        return showSkillDice(el, dice, mult, zorome, quick || ctl.auto, view.speed);
      },
      cutIn: (name: string, skill: string, side: string, cid?: FxLook, ougi?: boolean, enemyId?: string) => cutIn(el, name, skill, side, view, cid, ougi, enemyId),
      banner: (text: string, sub?: string) => banner(el, text, sub, view.speed),
      soul: (cell: number) => soulFly(el, view, cell),
      log: (text: string) => pushLog(el, text),
      // パレットの戦いでは、攻撃・術の相手を選んだときは札を出さない（減る分は相手の HP のゲージに出る）
      info: (cell: number | null) =>
        cell == null || (pal.on && ctl.ui?.target && refCell(ctl.board, ctl.ui.target) === cell) ? hideInfo(el, view) : showInfo(el, ctl.board, cell, view, ctl),
      error: (text: string) => toast(text, 'error'),
      saved: () => (prologue ? undefined : patch({ suspended: { stageId, charId, savedAt: Date.now() } })),
      over: (result: import('@hyakki/core').BattleResult, commands: Command[]) => void onOver(result, commands),
      surge: (e: SurgeEvent, heal: number, u?: VUnit) => surgeFx(el, ctl, e, heal, u, view),
      ougi: (enemyId: string, level: number, u?: VUnit) => ougiFx(el, ctl, enemyId, level, u, view),
      dash: async (e: { budget: number }) => {
        // 手動のときだけ一瞬止めて知らせる（案内は下の1行にも出る）
        surgeNote(el, `一撃離脱：あと${e.budget}マス動けます`, 'blue dash', view.speed);
        if (!ctl.auto) await sleep(350 / view.speed);
      },
    };
    try {
      ctl = new BattleController(bapi, view, { sessionId: save.sessionId, setup: save.setup, stageId, charId, local: save.local }, stage.terrain, hooks, save.commands);
    } catch (e) {
      el.querySelector('.board-msg')!.innerHTML = `<div class="state-box error"><p>戦闘を開始できませんでした。</p><small>${esc(String(e))}</small><button class="btn" data-act="leave">戻る</button></div>`;
      (el.querySelector('.board-msg') as HTMLElement).hidden = false;
      cleanups.push(onAct(el, { leave: () => go('stages') }));
      return cleanup;
    }
    if (resume && save.commands.length) toast('中断したところから再開します');
    // 開発用：撮影・確認のスクリプトから戦闘を進められるように
    if (import.meta.env.DEV) {
      (window as unknown as { __hkBattle?: unknown }).__hkBattle = {
        ctl,
        view,
        // 奥義の絵を読む（撮影で奥義を覚えた状態にしたとき用）
        ougiArt: () => (preloadCutin(charId), loadSheet(`ougi_${charId}`)),
      };
      cleanups.push(() => delete (window as unknown as { __hkBattle?: unknown }).__hkBattle);
    }
    // オートの目標（式神候補・龍脈）：オート周回はその設定、戦闘中のオートは端末の設定
    ctl.aiOpts = aiOptsOf(ar && autoRun() ? autoRun()!.cfg.ai : { shiki: !!state.settings.autoShiki, relays: !!state.settings.autoRelays });
    // 加勢の式神（右上の丸。押すと技・加護の説明）
    {
      const c = ctl.save.setup.loadout.companion;
      const box = el.querySelector<HTMLElement>('.b-comp')!;
      if (c && SHIKIGAMI[c.enemyId]) {
        box.innerHTML = `<button class="bc-face s${c.star}" aria-label="式神召喚 ${esc(shikiFormName(c.enemyId, c.star))}（押すと効果）">${shikiArtHtml(c.enemyId, 64, c.star)}</button>`;
        box.querySelector('button')!.addEventListener('click', (e) => {
          e.stopPropagation();
          openCompanionInfo(c.enemyId, c.star, c.level ?? 1);
        });
      }
    }
    // ---------- オート周回 ----------
    const arBar = el.querySelector<HTMLElement>('.ar-bar')!;
    const arWhere = el.querySelector<HTMLElement>('.ar-where')!;
    const drawArBar = () => {
      const r = autoRun();
      arBar.hidden = !ar || !r;
      arWhere.hidden = !ar || !r;
      if (!r) return;
      // 今どこを攻めているか（上の細い帯）：国・地方・陣・戦場、難易度、未踏へ挑戦か周回か
      const pl = placeOfStage(stageId);
      const where = pl ? provinceLabel(pl.province) : '';
      const slot = pl ? (pl.castle ? strongholdLabel(pl.province) : slotLabel(pl.slot)) : '';
      arWhere.innerHTML = `<em class="${r.phase === 'advance' ? 'adv' : 'farm'}">${r.phase === 'advance' ? '挑戦' : '周回'}</em>${icon('flag')}<span>${esc(where)}</span><small>${esc(slot)}</small><b>${esc(pl?.field ?? stage.name)}</b>${difficulty ? `<small class="df">${['通常', '上', '極', '鬼'][difficulty]}</small>` : ''}<small class="ar-grace">${icon('clock')}閉じても${farmGraceMs() / 60000}分進む</small>`;
      // ボタンは1度だけ作る（作り直すと押している途中の「周回をやめる」が効かない）
      if (!arBar.querySelector('[data-act="arstop"]'))
        arBar.innerHTML = `<span class="ar-tag">${icon('auto')}オート周回</span><span class="ar-info"></span><button class="btn danger small" data-act="arstop">${icon('stop')}<span>このバトルで終わる</span></button><button class="btn small" data-act="pson">節電</button>`;
      // 2行：1行目に戦数、2行目に勝敗と周回の段階（1行だと切れるため）
      const info = arBar.querySelector('.ar-info')!;
      const [head, ...rest] = (r.stopAfter ? 'このバトルで終わります' : autoRunLabel(r)).split('・');
      info.replaceChildren(Object.assign(document.createElement('span'), { textContent: head }), Object.assign(document.createElement('span'), { textContent: rest.join('・') }));
      stopBtnState(arBar.querySelector('[data-act="arstop"]'), r.stopAfter);
    };
    if (ar) {
      ctl.setAuto(true);
      if (fast) ctl.setTurbo(true);
      ctlRef.turbo = ctl.turbo;
      drawArBar();
      cleanups.push(onAutoRun(drawArBar));
      cleanups.push(onAutoRun(drawPs));
    }
    /** 待つ（残り時間を表示）。周回が止まったら false */
    const waitShown = async (ms: number, label: string, noClock = false) => {
      const overlay = el.querySelector<HTMLElement>('.board-msg')!;
      const until = Date.now() + ms;
      while (Date.now() < until) {
        if (!autoRun() || !alive) return false;
        const left = Math.ceil((until - Date.now()) / 1000);
        const txt = noClock ? label : `${label} ${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
        if (ps) {
          // 節電の札に出す（「戦果を確かめています…」の表示が残って重ならないように消す）
          overlay.hidden = true;
          setPs(txt);
        } else {
          overlay.hidden = false;
          overlay.innerHTML = loadingHtml(txt);
        }
        await sleep(Math.min(1000, until - Date.now()));
      }
      return !!autoRun();
    };
    const stopAr = (reason: string) => {
      stopAutoRun(reason);
      ctl.setTurbo(false);
      ctlRef.turbo = false;
      el.querySelector('.battle')!.classList.remove('autorun');
      drawArBar();
    };
    /** 節電の進み方：自分の手番 k の始まりを、今の手番から数えて1手番＝秒÷倍速ずつに */
    function setPace() {
      if (!state.profile) return;
      const msPerTurn = farmBattleMs(state.profile, 1);
      ctl.pace = { start: performance.now() - Math.max(0, ctl.b.stats.playerTurns - 1) * msPerTurn, msPerTurn };
    }
    /** 盤面を見ているオート周回を節電に（その場の続きから。以降の周回も節電） */
    function enterPowerSave() {
      if (fast || finished || !ar || !autoRun()) return;
      fast = true;
      autoRun()!.cfg.fast = true;
      patch({ settings: { ...state.settings, autoFast: true } });
      ps = el.querySelector('.battle')!.appendChild(Object.assign(document.createElement('div'), { className: 'ps-panel' }));
      el.querySelector('.battle')!.classList.add('powersave');
      psState = `戦闘中・${Math.max(1, ctl.b.stats.playerTurns)}手番目`;
      drawPs();
      ensureSkit(ctl.b.stats.playerTurns);
      setPace();
      ctl.setTurbo(true);
      ctlRef.turbo = true;
      hooks.changed();
    }
    /** 節電モードの1戦を盤面を見る1戦に（その場の続きから。以降の周回も盤面を見せる） */
    function showBoard() {
      if (!fast || finished) return;
      fast = false;
      mustWait = false;
      patch({ settings: { ...state.settings, autoFast: false } });
      const r = autoRun();
      if (r) r.cfg.fast = false;
      ctl.pace = null;
      if (save.sessionId && !save.local) void api.battleLive(save.sessionId).catch(() => {});
      skit?.destroy();
      skit = null;
      ps?.remove();
      ps = null;
      el.querySelector('.battle')!.classList.remove('powersave');
      ctl.setTurbo(false);
      ctlRef.turbo = false;
      hooks.changed();
    }
    /** 周回の途中で手動に（節電なら盤面も出す）。周回はこの1戦で終わり */
    function takeOver() {
      if (finished) return;
      showBoard();
      if (save.sessionId && !save.local) void api.battleLive(save.sessionId).catch(() => {});
      stopAr('手動に切り替えました');
      ctl.setAuto(false);
      hooks.changed();
    }
    /** 周回を止めずに途中の1戦を保存してTOPへ（「戦闘に戻る」でこの1戦から周回を続ける） */
    function park() {
      // 決着後（戦果の送信・兵糧の回復待ち）は退避する1戦が無い：周回を止めて TOP へ
      if (finished) {
        stopAr('停止しました');
        go('top');
        return;
      }
      const r = autoRun();
      if (r) r.parked = true;
      leavingForNext = true;
      ctl.persist();
      go('top');
    }
    /** 「このバトルで終わる」の入／切。決着後（戦果の送信中・兵糧の回復待ち）なら、その場で止めて成果へ */
    const askStopAr = () => {
      const r = autoRun();
      if (!r) return;
      if (finished && !finalWait) return stopAr('停止しました');
      setStopAfter(!r.stopAfter);
      toast(r.stopAfter ? 'このバトルが終わったら周回を終えます' : '周回を続けます');
    };
    // 賽3つの戦い（rules 2）：下のボタン列の代わりに六角のパレット。盤を押した所に開く
    /** パレットの入／切（設定・戦闘のメニュー）。切ると下のボタン列に戻る */
    const applyPal = () => {
      pal.on = (ctl.b.setup.rules ?? 0) >= 2 && state.settings.palette !== false;
      pal.key = '';
      pal.shown = '';
      el.querySelector('.battle')?.classList.toggle('pal-mode', pal.on);
      const box = el.querySelector<HTMLElement>('.actions')!;
      if (!pal.on && box.classList.contains('palette')) {
        box.classList.remove('palette');
        box.removeAttribute('style');
        box.hidden = false;
        for (const b of box.querySelectorAll<HTMLElement>(':scope > .act')) {
          b.hidden = false;
          b.removeAttribute('style');
          delete b.dataset.pal;
          b.classList.remove('blank');
        }
        box.querySelector('.pal-close')?.remove();
        box.querySelector('.pal-sim')?.remove();
        el.querySelector('.b-bottom')!.appendChild(box);
      }
      ctl.render();
    };
    applyPal();
    const palAt = (cell: number) => {
      if (!pal.on) return;
      // 相手・マスを選んだ押し方では動かさない（パレットはそのまま、見込みの小窓だけ変わる）
      if (ctl.ui?.target || ctl.ui?.build || ctl.ui?.mode) return void placePalette(el, ctl, view);
      pal.anchor = cell;
      pal.open = true;
      placePalette(el, ctl, view);
    };
    cleanups.push(
      bindBoardInput(view, {
        ...ctl.input,
        tap: (cell) => {
          ctl.input.tap(cell);
          palAt(cell);
        },
        dragEnd: (path, moved) => {
          ctl.input.dragEnd(path, moved);
          if (path.length) palAt(path[path.length - 1]);
        },
      }),
    );
    // チュートリアル（賽丸）：序盤の戦いで、場面が来たら操作を案内する（オート周回では出さない）
    if (!ar) cleanups.push(battleTutor(ctl, view, stageId));
    // メダリオン（妖力のゲージ）の長押し：奥義を構える／解く。押している間はリングが溜まる
    let armedAt = 0;
    {
      let timer = 0;
      const release = () => {
        clearTimeout(timer);
        timer = 0;
        medalPressing = false;
        el.querySelectorAll('.surge-chip.pressing, .hc-surge.pressing').forEach((x) => x.classList.remove('pressing'));
      };
      const down = (ev: PointerEvent) => {
        const chip = (ev.target as HTMLElement).closest<HTMLElement>('.surge-chip, .hc-surge');
        if (!chip || !el.contains(chip) || !ctl.board.ougi) return;
        if (!ctl.armed && !ctl.board.ougi.ready) return;
        medalPressing = true;
        chip.classList.add('pressing');
        timer = window.setTimeout(() => {
          release();
          if (ctl.arm()) {
            armedAt = Date.now();
            sfx(ctl.armed ? 'ougiCharge' : 'toggle', ctl.armed ? 0.7 : 1);
            if (ctl.armed) ougiArmFx(el, ctl.board.ougi?.name ?? '奥義', view);
          }
        }, LONG_PRESS_MS);
      };
      el.addEventListener('pointerdown', down);
      document.addEventListener('pointerup', release);
      document.addEventListener('pointercancel', release);
      // 長押しでブラウザのメニューが出ないように
      const ctx = (ev: Event) => (ev.target as HTMLElement).closest('.surge-chip, .hc-surge') && ev.preventDefault();
      el.addEventListener('contextmenu', ctx);
      cleanups.push(() => {
        release();
        el.removeEventListener('pointerdown', down);
        document.removeEventListener('pointerup', release);
        document.removeEventListener('pointercancel', release);
        el.removeEventListener('contextmenu', ctx);
      });
    }
    const acts: Record<string, (b: HTMLElement, ev: Event) => void> = {
        cmd: (b) => void ctl.command(b.dataset.cmd!, b.dataset.arg),
        skipwave: () => ctl.skipToWave(),
        buildmenu: () => toggleBuildMenu(el, ctl),
        itemmenu: () => openItemMenu(ctl),
        speed: () => cycleSpeed(),
        arstop: () => askStopAr(),
        psview: () => showBoard(),
        pson: () => enterPowerSave(),
        manual: () => takeOver(),
        park: () => park(),
        auto: () => toggleAuto(),
        pause: () => pauseMenu(ctl),
        menu: () => pauseMenu(ctl),
        closeinfo: () => hideInfo(el, view),
        demolish: async (b) => {
          const cell = b.dataset.cell;
          if (!cell) return;
          if (!(await confirmDialog('この建物を壊しますか？（陣材は戻りません。手番は使いません）', { ok: '壊す', danger: true }))) return;
          void ctl.command('demolish', cell);
        },
        palclose: () => {
          pal.open = false;
          placePalette(el, ctl, view);
        },
        surgeinfo: () => {
          // 長押しで奥義を構えた直後の click は詳細を開かない
          if (Date.now() - armedAt < 600) return;
          // 案内の途中（長押しを待つ所など）は詳細を開かない（開くと案内が止まり、閉じられなくなる）
          if (coaching()) return;
          surgeDialog(ctl);
        },
        terrinfo: () => terrDialog(ctl),
    };
    cleanups.push(onAct(el, acts));
    // 周回・オート・メニュー・速さのボタンは、指を離した時点で動かす（オート中は表示がよく描き直されるので、
    // 押している途中にボタンが作り直されると iPhone で click が届かない。押したボタンの種類で照らし合わせる）
    {
      const QUICK = new Set(['arstop', 'pson', 'auto', 'menu', 'speed']);
      let downAct = '';
      let firedAt = 0;
      const actOf = (e: Event) => (e.target as HTMLElement).closest<HTMLButtonElement>('[data-act]');
      const down = (e: PointerEvent) => (downAct = actOf(e)?.dataset.act ?? '');
      const up = (e: PointerEvent) => {
        const b = actOf(e);
        const a = b?.dataset.act ?? '';
        if (!b || b.disabled || a !== downAct || !QUICK.has(a) || !el.contains(b)) return;
        downAct = '';
        firedAt = Date.now();
        acts[a](b, e);
      };
      // 指を離した時点で動かしたものは、あとから来る click では動かさない
      const click = (e: Event) => {
        const a = actOf(e)?.dataset.act ?? '';
        if (QUICK.has(a) && Date.now() - firedAt < 800) {
          e.stopPropagation();
          e.preventDefault();
        }
      };
      el.addEventListener('pointerdown', down, true);
      el.addEventListener('pointerup', up, true);
      el.addEventListener('click', click, true);
      cleanups.push(() => {
        el.removeEventListener('pointerdown', down, true);
        el.removeEventListener('pointerup', up, true);
        el.removeEventListener('click', click, true);
      });
    }
    function setSpeed(next: (typeof SPEEDS)[number]) {
      if (fixedSpeed) return;
      speed4Tease = false;
      if (next > maxSpeed()) {
        // ゲストの×2はその場で連携できる小窓、×4は倍速パック（ショップ）。×4は今の速度のまま
        if (next === 2) guestBlocked('×2は、Google連携すると使えます。', 'speed');
        else toast('×4は「倍速パック」が必要です（ショップ）');
        next = next === 2 ? 1 : battleSpeedNow();
      }
      patch({ settings: { ...state.settings, battleSpeed: next } });
      applyViewSettings(view);
      hooks.changed();
    }
    function cycleSpeed() {
      if (fixedSpeed) return;
      // ×2 → 錠つきの×4（速度は×2のまま） → ×1
      if (speed4Tease) return setSpeed(1);
      const cur = battleSpeedNow();
      const next = SPEEDS[(SPEEDS.indexOf(cur) + 1) % SPEEDS.length];
      if (next === 4 && maxSpeed() === 2) {
        speed4Tease = true;
        toast('×4は「倍速パック」が必要です（ショップ）');
        hooks.changed();
        return;
      }
      setSpeed(next);
    }
    /** オートの切り替え。true を返したら切り替えた（権利が無いときはショップへ案内） */
    function toggleAuto(): boolean {
      // オート周回中：オート（盤面）→ 節電。節電の画面から盤面・手動へ
      if (autoRun() && ar) {
        enterPowerSave();
        return false;
      }
      if (guestBlocked('オートは、Google連携すると使えます。', 'auto')) return false;
      ctl.setAuto(!ctl.auto);
      return true;
    }
    // 盤面を見るオート周回を裏に回した：描かれない演出を待たずに、節電と同じ進み方（1手番＝秒÷倍速）で進める。戻ったら盤面を合わせて演出つきに戻す
    let bgRun = false;
    const bgOn = () => {
      if (bgRun || fast || finished || !ar || !autoRun()) return;
      bgRun = true;
      view.flushAnims();
      setPace();
      ctl.setTurbo(true);
      ctlRef.turbo = true;
    };
    const bgOff = () => {
      if (!bgRun) return;
      bgRun = false;
      if (fast) return; // 裏の間に節電へ切り替えた
      ctl.pace = null;
      if (!finished) ctl.setTurbo(false);
      ctlRef.turbo = false;
    };
    // タブを隠したら一時停止して保存（画面消灯・アプリ切替）。戻ったら、隠したことで止めた分だけ再開する
    // （以前は再開せず、メニューを開閉するまで操作が効かなかった。プロローグでは案内がメニューを止めるので詰まっていた）
    let hidPaused = false;
    let hiddenAt = 0;
    /** 周回中に1分以上画面を離れていた：この1戦はそのまま続け、終わったら止まっていたあいだの分をサーバーから受け取る */
    let settleAfter = false;
    // 裏に回っている間は「まだ開いている」を1分ごとにサーバーへ（裏のタブは処理が間引かれて周回が進まないので、戻ったらその時刻までをサーバーが計算する）
    let aliveTimer = 0;
    const vis = () => {
      // オート周回：画面を消していた（アプリ切替でブラウザが止まっていた）。途中の1戦は捨てない
      if (ar && document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        window.clearInterval(aliveTimer);
        aliveTimer = window.setInterval(() => autoRun() && void api.farmAlive().catch(() => {}), 60_000);
      } else if (ar && document.visibilityState === 'visible' && hiddenAt) {
        window.clearInterval(aliveTimer);
        if (Date.now() - hiddenAt > 60_000) settleAfter = true;
        hiddenAt = 0;
      }
      if (document.visibilityState === 'hidden') bgOn();
      else bgOff();
      if (document.visibilityState === 'hidden' && !finished) {
        ctl.persist();
        if (!ctl.auto && !ctl.paused) {
          ctl.setPaused(true);
          hidPaused = true;
        }
      } else if (document.visibilityState === 'visible' && hidPaused) {
        hidPaused = false;
        if (ctl.paused) ctl.setPaused(false);
      }
    };
    document.addEventListener('visibilitychange', vis);
    cleanups.push(() => window.clearInterval(aliveTimer));
    cleanups.push(() => document.removeEventListener('visibilitychange', vis));
    cleanups.push(() => {
      alive = false;
      if (!finished) {
        ctl.persist();
        patch({ suspended: { stageId, charId, savedAt: Date.now() } });
      }
      // 画面を離れたらオート周回は止める（次の戦闘へ移るときを除く）
      if (ar && !leavingForNext) stopAutoRun('画面を離れたため停止しました');
      ctl.stop();
    });
    hooks.changed();
    // 裏に回ったまま次の1戦へ移った
    if (document.visibilityState === 'hidden') bgOn();
    if (!resume && !fast && !bgRun) {
      // 防衛戦は特別ルールを最初に説明する（「次回から表示しない」なら1秒の告知だけ。オート周回中は止めない）
      const def = ctl.board.defense;
      if (def) await defenseIntro(el, def, view.speed, !!(ar && autoRun()));
      // 砦は手番の上限（短期決戦）を最初に知らせる
      const tl = ctl.board.turnLimit;
      if (tl) await banner(el, 'ルール「短期決戦」', `${tl.limit}手番のうちに敵本陣を落とす（過ぎると負け）`, view.speed, ar && autoRun() ? 1200 : undefined);
      // 状態異常のある陣地ルールは最初に知らせる
      const rule = bapi.territoryRule(ctl.b);
      if (rule?.footing.afflict) await banner(el, `陣地ルール「${rule.name}」`, rule.desc, view.speed);
    }
    // 保存から続けた1戦は、最初の出撃からの時間がわからないので端末では待たない（サーバーが too_early で待たせる）
    const startedAt = resume ? 0 : Date.now();
    // 節電モード：盤面は裏で手番ごとに進む（1戦の時間は手番数×秒÷倍速）。表示は手番だけ
    // 盤面を見せるオート周回は最低時間なし（盤面で決着したら次へ）
    if (fast) setPace();
    if (ar) {
      const tick = window.setInterval(() => {
        if (finished || !alive) return window.clearInterval(tick);
        if (fast && psState.startsWith('戦闘中')) setPs(`戦闘中・${Math.max(1, ctl.b.stats.playerTurns)}手番目`);
        // 手番が進んだら寸劇を1回だけ動かす
        const t = ctl.b.stats.playerTurns;
        if (skit && t > skitTurn) {
          skitTurn = t;
          void skit.turn();
        }
      }, 1000);
      cleanups.push(() => window.clearInterval(tick));
    }
    // 始めた時点で手元にも保存（最初の操作の前に閉じても、TOP の「戦闘に戻る」から続けられるように）
    if (!saved?.local) ctl.persist();
    ctl.start();

    async function onOver(result: import('@hyakki/core').BattleResult, commands: Command[]) {
      finished = true;
      boardFrozen = true;
      // 決着後（戦果の送信・兵糧の回復待ち）は盤面への切り替え・退避はできない（「このバトルで終わる」で止める）
      ps?.querySelector('.ps-box .btn-row')?.remove();
      patch({ suspended: null });
      if (prologue) {
        // プロローグ：勝てば「三百年後」へ（負けたら最初から。強さは負けない程度にしてある）
        clearSaved();
        void bgm(null, { fade: 0.6 });
        // 勝利のジングルは鳴らさない（長く、続く宴〜王が消える夜の場面に重なるため）
        if (result.win) track('prologue.win');
        await banner(el, result.win ? '天下統一' : '退却', result.win ? '人の世の最後の城、落つ' : 'もう一度', 1, undefined, result.win ? 'win' : 'lose');
        go('prologue', { step: result.win ? 'after' : 'battle' }, true);
        return;
      }
      if (skit && result.win) void skit.win();
      const running = ar && !!autoRun();
      // 周回中は曲を止めずに続ける（毎戦ジングルで途切れないように）
      if (!running) {
        void bgm(null, { fade: 0.3 }); // 戦果の確認が長引いても合戦の曲に戻らないように
        void jingle(result.win ? 'victory' : 'defeat');
      }
      if (!fast && !bgRun) await banner(el, result.win ? '勝利' : '敗北', result.win ? (result.reason === 'defense' ? '本陣を守り抜いた' : '敵本陣を攻め落とした') : result.reason === 'timeout' ? '手番が尽きた' : '自本陣が落ちた', 1, undefined, result.win ? 'win' : 'lose');
      // 節電モード：1戦の時間（手番数×秒÷倍速）が経つまで待ってから送る（サーバーも確かめる）
      if (running && fast) {
        // 節電で始めた1戦は出撃からの時間、途中で節電にした1戦は節電の進み方（手番数×秒÷倍速）の終わりまで待つ
        const pace = ctl.pace;
        const left = mustWait
          ? farmBattleMs(state.profile!, result.playerTurns) - (Date.now() - startedAt)
          : pace ? pace.start + Math.max(1, result.playerTurns) * pace.msPerTurn - performance.now() : 0;
        if (left > 0) {
          // 待つあいだに止めた・画面を離れたときも、この1戦（勝ったかもしれない）は捨てずに送る（早すぎればサーバーの too_early で待つ）
          finalWait = true;
          await waitShown(left, `戦闘中・${Math.max(1, result.playerTurns)}手番目`, true);
          finalWait = false;
        }
      }
      let server = null;
      let error: string | undefined;
      if (save.sessionId && !save.local) {
        const overlay = el.querySelector<HTMLElement>('.board-msg')!;
        // 節電は札の状態の行に出す（盤面用の表示を重ねない）
        if (ps) setPs('戦果を確かめています…');
        else {
          overlay.hidden = false;
          overlay.innerHTML = loadingHtml('戦果を確かめています…');
        }
        let early = 0;
        for (let i = 0; i < 3 && !server; i++) {
          try {
            server = await api.battleFinish(save.sessionId, commands);
          } catch (e) {
            // 1戦の時間がまだ（端末とサーバーの時計の差）：言われた分だけ待って送り直す
            if (e instanceof ApiError && e.code === 'too_early' && early++ < 5) {
              await sleep(Math.min(60_000, Number((e.body as { waitMs?: number })?.waitMs ?? 2000) + 500));
              i--;
              continue;
            }
            error = errorText(e);
            if (e instanceof ApiError && !e.offline && e.status < 500) break;
            await sleep(1500 * (i + 1));
          }
        }
        if (server) {
          error = undefined;
          await refreshMe().catch(() => {});
        } else {
          // 送れなかった結果は保存して、リザルト画面から再送できるようにする
          try {
            localStorage.setItem('hk.finish', JSON.stringify({ sessionId: save.sessionId, commands, stageId, charId, difficulty }));
          } catch {
            /* 無視 */
          }
        }
      }
      clearSaved();
      setResult({ stageId, charId, difficulty, result: server?.result ?? result, server, local: !!save.local, error, storyEligible: !ar });
      // オート周回：結果を記録して次の戦場へ（止まったらリザルトでまとめて見せる）
      if (running && autoRun()) {
        if (!server) stopAutoRun(`戦果を送れなかったため停止しました（${error ?? ''}）`);
        else {
          recordAutoBattle(stageId, (server.result ?? result).win, server);
          if (settleAfter) {
            settleAfter = false;
            try {
              const res = await api.farmSettle();
              await refreshMe().catch(() => {});
              recordSettledRuns(res);
              if (res.battles) toast(`閉じていたあいだに ${res.battles}戦 周回しました`);
            } catch {
              /* 受け取れなくても周回は続ける */
            }
          }
        }
        if (autoRun()?.stopAfter) stopAutoRun('このバトルで終えました');
        if (autoRun()) {
          let next = nextAutoStage();
          // 兵糧が尽きた：止めずに回復を待つ
          while (!('stop' in next) && next.waitMs && autoRun()) {
            if (!(await waitShown(next.waitMs, '兵糧の回復を待っています'))) break;
            await refreshMe().catch(() => {});
            next = nextAutoStage();
          }
          if (!autoRun()) {
            /* 待つあいだに止めた */
          } else if ('stop' in next) stopAutoRun(next.stop);
          else if (alive) {
            leavingForNext = true;
            go('battle', { stage: next.stageId, char: charId, diff: 0, autorun: 1 }, true);
            return;
          }
        }
      }
      // 待つあいだに別の画面へ移っていたら連れ戻さない（戦果は送り済み・リザルトは残っている）
      if (alive) go('result', {}, true);
    }

    /** メニュー（≡／一時停止）：速度・オート・星条件・陣材を見ながら、再開／保存してTOP／降参 */
    function pauseMenu(c: BattleController) {
      c.setPaused(true);
      const settingsHtml = () => {
        const sp = battleSpeedNow();
        const b = c.board;
        const tb = Math.round(b.territory.blue * 100);
        return `${fixedSpeed ? '' : `<div class="bm-row"><span class="bm-k">${icon('speed')}速度</span><div class="seg">${SPEEDS.map((n) => `<button class="${n === sp ? 'on' : ''}" data-p="sp${n}" aria-pressed="${n === sp}">×${n}${n > maxSpeed() ? icon('lock') : ''}</button>`).join('')}</div></div>`}
          ${(c.b.setup.rules ?? 0) >= 2 ? `<div class="bm-row"><span class="bm-k">${icon('dice')}操作</span><div class="seg"><button class="${state.settings.palette !== false ? 'on' : ''}" data-p="pal1">盤の上</button><button class="${state.settings.palette === false ? 'on' : ''}" data-p="pal0">下のボタン</button></div></div>` : ''}
          <div class="bm-row"><span class="bm-k">${icon('auto')}オート</span>${ar && autoRun() ? `<button class="btn small" data-p="manual">手動で戦う</button><button class="btn small danger" data-p="arstop">${autoRun()?.stopAfter ? '周回を続ける' : 'このバトルで終わる'}</button>` : `<button class="btn small ${c.auto ? 'primary' : ''}" data-p="auto" aria-pressed="${c.auto}">${c.auto ? '入' : '切'}</button>`}</div>
          <div class="bm-res"><span>${icon('wall')}陣材 <b>${fmt(b.materials)}</b></span><span>${icon('potion')}道具 <b>${b.items.reduce((s, x) => s + x.count, 0)}</b></span><span>${icon('flag')}陣地 <b>${tb}%</b></span></div>
          <ul class="star-list">${starConds(b, view).map((x) => `<li class="${x.on ? 'on' : ''}"><i class="star">★</i>${x.text}</li>`).join('')}</ul>
          ${surgePanelHtml(b, true)}`;
      };
      const m = openModal(
        `<div class="bmenu"><p class="bm-place">${esc(`${placeOfStage(stage.id)?.province.name ?? ''} ${placeOfStage(stage.id)?.field ?? stage.name}`)}</p><div class="bm-set">${settingsHtml()}</div>
        <div class="btn-col">
          <button class="btn primary" data-p="resume">再開</button>
          <button class="btn" data-p="help">${icon('scroll')}あそびかた</button>
          <button class="btn" data-p="top">保存してTOPへ</button>
          <button class="btn danger" data-p="giveup">降参する</button>
        </div>
        <p class="muted small">戦闘は手番ごとに端末へ保存されます。TOPの「戦闘に戻る」から再開できます。</p></div>`,
        { title: 'メニュー（一時停止中）', onClose: () => c.paused && c.setPaused(false) },
      );
      const redraw = () => (m.body.querySelector('.bm-set')!.innerHTML = settingsHtml());
      m.body.addEventListener('click', async (e) => {
        const p = (e.target as HTMLElement).closest<HTMLElement>('[data-p]')?.dataset.p;
        if (p === 'resume') m.close();
        else if (p === 'help') openHelp('battle');
        else if (p === 'pal1' || p === 'pal0') {
          setSetting('palette', p === 'pal1');
          applyPal();
          redraw();
        }
        else if (p?.startsWith('sp')) {
          setSpeed(Number(p.slice(2)) as (typeof SPEEDS)[number]);
          redraw();
        } else if (p === 'arstop') {
          m.close();
          askStopAr();
        } else if (p === 'manual') {
          m.close();
          takeOver();
        } else if (p === 'auto') {
          if (toggleAuto() && c.auto) m.close();
          else redraw();
        } else if (p === 'top') {
          m.close();
          if (ar && autoRun()) return park();
          c.persist();
          go('top');
        } else if (p === 'giveup') {
          const ok = await confirmDialog('降参すると敗北になり、消費した兵糧は戻りません。よろしいですか？', { ok: '降参する', danger: true });
          if (!ok) return;
          finished = true;
          m.close();
          clearSaved();
          patch({ suspended: null });
          if (save.sessionId) void api.battleAbandon(save.sessionId).catch(() => {});
          // 周回中の降参：周回もここで終わり、成果を見せる
          if (ar && autoRun()) {
            stopAutoRun('降参しました');
            go('result', {}, true);
            return;
          }
          go('stages');
        }
      });
    }

    return cleanup;
  },
});

/** その戦場に出る敵の章シート・建物を読む（最大4秒待つ。間に合わなければ図形で描いて、読めたら次の描画から絵になる） */
function preloadStage(stage: { enemyIds: string[]; bossId?: string | null; terrain?: string }) {
  return loadSheets(stageSheets([...stage.enemyIds, ...(stage.bossId ? [stage.bossId] : [])], stage.terrain), undefined, 4000);
}

/** 速度を×1に固定する戦闘か（プロローグ） */
let fixedSpeed = false;
/** 演出（カットイン・妖力の告知）を省くか：演出速度が最速、または速度×4以上 */
const skipFx = (view: BoardView) => !fixedSpeed && (state.settings.fxSpeed >= 3 || view.speed >= 4);

function applyViewSettings(view: BoardView) {
  view.speed = fixedSpeed ? 1 : battleSpeedNow() * state.settings.fxSpeed;
  view.reduced = state.settings.reduceShake;
}

// ---------- HTML ----------
/** 手順の段（賽→移動→建築→行動）。案内文の頭に今の段だけ出す */
const STEPS = ['賽', '移動', '建築', '行動'] as const;

/* 画面の組み立て：Canvas（戦場の地面＋盤面）を画面全体に敷き、その上に HUD を重ねる。
   上＝札・TURN・行動順・≡（2行目に本陣HP）、下＝案内1行＋行動ボタン1行。PC は左右に情報欄。 */
function shellHtml(name: string, stage?: { id: number; chapter: number; index: number }) {
  return `
  <div class="battle">
    <div class="board-wrap">
      <canvas class="board" aria-label="ヘックスの戦場"></canvas>
      <div class="floaters" aria-hidden="true"></div>
      <div class="dice-pop" hidden><div class="dice-row"><div class="die"></div></div><p class="dice-cap"></p></div>
      <div class="cutin" hidden></div>
      <div class="surge-cut" hidden></div>
      <div class="ougi-cut" hidden aria-hidden="true"></div>
      <div class="surge-note" hidden aria-live="polite"></div>
      <div class="b-banner" hidden></div>
    </div>
    <header class="b-top">
      <div class="ar-where" hidden></div>
      <div class="b-row1">
        <h1 class="sr-only">${esc(stage ? `${placeOfStage(stage.id)?.province.name ?? ''} ${name}` : name)}</h1>
        <div class="b-order" aria-label="行動順"></div>
        <div class="b-res" aria-label="陣材・薬・陣地率"></div>
        <div class="b-ctrl">
          <button class="speed-chip sp" data-act="speed" aria-label="速度"></button>
          <button class="icon-btn ctrl pc" data-act="speed" aria-label="速度"></button>
          <button class="icon-btn ctrl auto" data-act="auto" aria-label="オート">${icon('auto')}${guestLockHtml(icon('lock'))}</button>
          <button class="icon-btn ctrl pc" data-act="pause" aria-label="一時停止">${icon('pause')}</button>
          <button class="icon-btn ctrl sp" data-act="menu" aria-label="メニュー（一時停止・速度・オート）">${icon('menu')}</button>
        </div>
      </div>
      <div class="b-cores"></div>
      <div class="b-surge"></div>
      <div class="b-comp"></div>
      <div class="ar-bar" role="status" hidden></div>
    </header>
    <aside class="b-side left">
      <section class="side-card hero-card" aria-label="選択中のユニット"></section>
      <section class="side-card"><h3 class="sec-title">星条件</h3><ul class="star-list"></ul></section>
    </aside>
    <aside class="b-side right">
      <section class="side-card info-slot"><h3 class="sec-title">詳細</h3><p class="muted small">マスを押すと詳細が出ます</p></section>
      <section class="side-card"><h3 class="sec-title">陣地</h3><div class="terr"></div><div class="cores"></div></section>
      <section class="side-card log"><h3 class="sec-title">戦況</h3><ol class="b-log"></ol></section>
    </aside>
    <div class="info-card" hidden></div>
    <footer class="b-bottom">
      <div class="b-guide"><p class="hint" aria-live="polite">${icon('flag')}<b class="step" hidden></b><span class="txt"></span></p><ul class="legend" aria-hidden="true"></ul></div>
      <div class="actions" role="toolbar" aria-label="行動"></div>
    </footer>
    <div class="board-msg" hidden></div>
  </div>`;
}

/** HUD の大きさを測って、盤を置ける空き（Canvas 上の余白）を BoardView に渡す */
function layoutBoard(el: HTMLElement, view: BoardView) {
  const root = el.querySelector<HTMLElement>('.battle');
  if (!root) return;
  const R = root.getBoundingClientRect();
  const rect = (q: string) => {
    const e = el.querySelector<HTMLElement>(q);
    if (!e || !e.offsetParent) return null;
    const r = e.getBoundingClientRect();
    return r.width > 0 && r.height > 0 ? r : null;
  };
  const top = rect('.b-top');
  const bot = rect('.b-bottom');
  const left = rect('.b-side.left');
  const right = rect('.b-side.right');
  const t = top ? top.bottom - R.top + 2 : 0;
  const b = bot ? R.bottom - bot.top + 2 : 0;
  const l = left ? left.right - R.left + 8 : 0;
  const r = right ? R.right - right.left + 8 : 0;
  root.style.setProperty('--bt', `${Math.round(t)}px`);
  root.style.setProperty('--bb', `${Math.round(b)}px`);
  if (!view.setInsets(t, r, b, l)) view.resize();
}

/** 防衛戦の波の札：準備中は次の波までの手番、波が来たら第何波・残りの攻め手 */
function waveChip(d: NonNullable<VBoard['defense']>) {
  const text = d.wave === 0 ? `準備 あと${d.nextIn}手番` : `第${d.wave}波/${d.total}・残り${d.attackers}${d.nextIn != null ? `・次まで${d.nextIn}` : ''}`;
  return `<span class="tug-core r wave ${d.wave > 0 ? 'on' : ''}" title="防衛戦" aria-label="防衛戦 ${esc(text)}"><b>${esc(text)}</b></span>`;
}

/** 自本陣の守り（鬼の棟梁）：被ダメの減り（-30% など）の盾。守りが無ければ空 */
function guardChip(m: number) {
  if (!(m < 1)) return '';
  const pct = Math.round((1 - m) * 100);
  return `<i class="core-guard" title="本陣の守り：被ダメ-${pct}%" aria-label="本陣の守り 被ダメ-${pct}%">${icon('shield')}-${pct}%</i>`;
}

/** 砦の手番の札：残りの手番（上限のない戦いは空） */
function turnChip(t: VBoard['turnLimit']) {
  if (!t) return '';
  return `<span class="tug-core r turns ${t.left <= 3 ? 'on' : ''}" title="手番の上限 ${t.limit}" aria-label="残り${t.left}手番（上限${t.limit}）">${icon('wait')}<b>${t.left}</b></span>`;
}

function starConds(board: VBoard, view: BoardView) {
  const vm = view.vm;
  const relays = board.tiles.map((t, i) => (t.relay ? i : -1)).filter((i) => i >= 0);
  const ownRelays = relays.filter((i) => (vm?.owners[i] ?? board.tiles[i].owner) === 'blue').length;
  const ko = board.heroKOs;
  return [
    { on: false, text: board.defense ? `第${board.defense.total}波まで守り抜く` : '敵本陣を攻略' },
    { on: ko === 0, text: ko === 0 ? '戦闘不能なし' : `戦闘不能 ${ko}回` },
    { on: relays.length > 0 && ownRelays === relays.length, text: `龍脈 ${ownRelays}/${relays.length}` },
  ];
}

const isPc = () => designWidth() >= 1100;

function renderHud(el: HTMLElement, board: VBoard, ctl: BattleController | null, view: BoardView) {
  const vm = view.vm;
  const coreHp = (side: 'red' | 'blue') => Math.max(0, vm ? vm.cores[side] : board.cores[side].hp);
  const corePct = (side: 'red' | 'blue') => Math.max(0, Math.min(100, (coreHp(side) / board.cores[side].max) * 100));
  // 本陣HP（PC：右の陣地欄、スマホ：上の2行目に細く1行）
  const coreRow = (side: 'red' | 'blue') =>
    `<div class="core-hp ${side}"><span>${side === 'red' ? '敵<i>本陣</i>' : '自<i>本陣</i>'}</span><div class="hpbar"><i style="width:${corePct(side)}%"></i></div><b>${fmt(coreHp(side))}</b>${side === 'blue' ? guardChip(board.coreGuard) : ''}</div>`;
  const coresHtml =
    (board.defense ? `<div class="core-hp red wave-row">${waveChip(board.defense)}</div>` : coreRow('red')) + coreRow('blue') + (board.turnLimit ? `<div class="core-hp red wave-row">${turnChip(board.turnLimit)}</div>` : '');
  el.querySelector('.cores')!.innerHTML = coresHtml;
  const tb = Math.round(board.territory.blue * 100);
  const tr = Math.round(board.territory.red * 100);
  const spLabel = ctl?.turbo ? '高速' : `×${speed4Tease ? 4 : battleSpeedNow()}`;
  const mySurge = board.surge?.blue;
  const og = ougiState(board, ctl);
  // 2行目（スマホ）：本陣HPと陣地の綱引き1本（左＝自分、右＝敵）。陣材は建築ボタンの数字
  const tug = el.querySelector<HTMLElement>('.b-cores')!;
  const note = tug.querySelector('.tug-note')?.outerHTML ?? '';
  // 段階の閾値：自陣は左から、敵陣は右から、効果のアイコン（届いた段は光る）。50%の段は真ん中に1つ（取った側の色）
  const ticks = board.terrTiers
    .map((t) => {
      const b = board.territory.blue >= t.share;
      const r = board.territory.red >= t.share;
      if (t.share >= 0.5) return `<span class="tk c ${b ? 'b on' : r ? 'r on' : ''}" style="left:50%">${icon(t.icon)}</span>`;
      return `<span class="tk b ${b ? 'on' : ''}" style="left:${t.share * 100}%">${icon(t.icon)}</span><span class="tk r ${r ? 'on' : ''}" style="right:${t.share * 100}%">${icon(t.icon)}</span>`;
    })
    .join('');
  tug.innerHTML = `<div class="tug" role="button" tabindex="0" data-act="terrinfo" aria-label="陣地（自陣${tb}%・敵陣${tr}%）・詳細">
      <span class="tug-core b ${coreHp('blue') < board.cores.blue.max ? 'hurt' : ''}" title="自本陣">${icon('castle')}<b>${fmt(coreHp('blue'))}</b>${guardChip(board.coreGuard)}</span>
      <div class="tug-track"><div class="tug-bar"><i class="b" style="width:${tb}%"></i><i class="r" style="width:${tr}%"></i><em class="tb">${tb}%</em><em class="tr">${tr}%</em></div><div class="tug-ticks" aria-hidden="true">${ticks}</div></div>
      ${board.defense ? waveChip(board.defense) : `<span class="tug-core r ${coreHp('red') < board.cores.red.max ? 'hurt' : ''}" title="敵本陣"><b>${fmt(coreHp('red'))}</b>${icon('castle')}</span>`}
      ${turnChip(board.turnLimit)}
      ${note}
    </div>`;
  // 妖力のゲージ（スマホ）：綱引きバーの下、左に大きめに（盤の左上に重ねる）
  setHtml(el.querySelector('.b-surge')!, (mySurge ? surgeChip(mySurge, false, og) : '') + luckList(board));
  const sp = el.querySelector<HTMLElement>('.speed-chip.sp');
  if (sp) {
    sp.innerHTML = `${icon('speed')}<b>${spLabel}</b>${speedLockHtml()}`;
    sp.setAttribute('aria-label', `速度 ${spLabel}`);
  }
  // 星条件（戦闘中の見込み）
  const hero = board.units.find((u) => u.kind === 'hero');
  el.querySelector('.star-list')!.innerHTML = starConds(board, view)
    .map((c) => `<li class="${c.on ? 'on' : ''}"><i class="star">★</i>${c.text}</li>`)
    .join('');
  // 陣地ゲージ
  el.querySelector('.terr')!.innerHTML = `<div class="terr-num"><span class="b">自陣 <b>${tb}</b>%</span><span class="r">敵陣 <b>${tr}</b>%</span></div>
    <div class="terr-bar"><i class="b" style="width:${tb}%"></i><i class="r" style="width:${tr}%"></i></div>
    ${board.tier.blue ? `<small class="muted">陣地 段階${board.tier.blue}${board.ruleName ? `・${esc(board.ruleName)}` : ''}</small>` : board.ruleName ? `<small class="muted">${esc(board.ruleName)}</small>` : ''}
    ${board.ruleDesc ? `<small class="muted terr-rule">${esc(board.ruleDesc)}</small>` : ''}`;
  // 陣材・薬・陣地率（PC）
  el.querySelector('.b-res')!.innerHTML = `<span title="陣材">${icon('wall')}<small>陣材</small><b>${fmt(board.materials)}</b></span>
    <span title="道具">${icon('potion')}<small>道具</small><b>${board.items.reduce((s, x) => s + x.count, 0)}</b></span>
    <span title="陣地率">${icon('flag')}<small>陣地率</small><b>${tb}%</b></span>${mySurge ? surgeChip(mySurge, true, og) : ''}`;
  // 行動順（顔・敵の絵を矢印でつなぐ）
  const pc = isPc();
  const order = board.order.map((id) => board.units.find((u) => u.id === id)).filter((u): u is VUnit => !!u);
  setHtml(el.querySelector('.b-order')!, `<span class="turn"><small>TURN</small><b>${Math.max(1, board.turn)}</b></span><span class="ords">${order
    .slice(0, 6)
    .map((u, i) => `${i ? '<i class="arr" aria-hidden="true"></i>' : ''}<span class="ord ${u.side} ${i === 0 ? 'now' : ''}" title="${esc(u.name)}">${unitIcon(u, pc ? (i === 0 ? 38 : 30) : i === 0 ? 30 : 24)}</span>`)
    .join('')}</span>`);
  // 選択中ユニット（自分のキャラ。PCの左欄）
  const hc = el.querySelector<HTMLElement>('.hero-card')!;
  if (hero) {
    const c = hero.charId ? CHARS[hero.charId] : null;
    const hp = vm?.units.get(hero.id)?.hp ?? hero.hp;
    const st = hero.stats;
    const wait = hero.skillWait ?? 0;
    const sealed = !!hero.status?.some((x) => x.key === 'seal');
    hc.innerHTML = `<div class="hc-top">${hero.charId ? `<span class="face hc-face" style="${faceStyle(hero.charId)}"></span>` : ''}
        <div class="hc-name"><b>${esc(c?.name ?? hero.name)}</b>${hero.lv ? `<small>Lv.${hero.lv}</small>` : ''}</div></div>
      <div class="hc-hp"><span>HP</span><div class="hpbar"><i style="width:${Math.max(0, Math.min(100, (hp / hero.max) * 100))}%"></i></div><b>${fmt(Math.max(0, hp))}<small>/${fmt(hero.max)}</small></b></div>
      <div class="hc-stats">
        <span>${icon('attack')}攻<b>${st.atk ?? '—'}</b></span><span>${icon('shield')}防<b>${st.def ?? '—'}</b></span>
        <span>${icon('feather')}速<b>${st.agi ?? '—'}</b></span><span>${icon('range')}射程<b>${st.range ?? '—'}</b></span>
      </div>
      ${hero.skillName ? `<div class="hc-skill ${wait || sealed ? 'wait' : 'ready'}">${icon('skill')}<span>${esc(hero.skillName)}</span><em>${sealed ? '封印中' : wait ? `あと${wait}手番` : '使用可'}</em></div>` : ''}
      ${statusChips(hero)}
      ${mySurge ? `<button class="hc-surge s${mySurge.stage} ${og}${medalPressing ? ' pressing' : ''}" data-act="surgeinfo">${flamesHtml(mySurge)}<span class="hs-t"><b>${esc(surgeText(mySurge, false))}</b><small>${mySurge.toNext != null ? `次まで あと${mySurge.toNext}` : '最大'}</small></span>${og === 'armed' ? `<em class="og">奥義の構え</em>` : og === 'ready' ? `<em class="og">長押しで奥義</em>` : mySurge.traits.length ? `<em>${esc(mySurge.traits.map((t) => t.name).join('・'))}</em>` : ''}</button>` : ''}`;
  }
  // 速度・自動（PCの上のアイコン）
  el.querySelectorAll<HTMLElement>('.b-ctrl [data-act="speed"]').forEach((sp) => (sp.innerHTML = `${icon('speed')}<small>${spLabel}</small>${speedLockHtml()}`));
  const autoBtn = el.querySelector<HTMLElement>('.b-ctrl [data-act="auto"]')!;
  autoBtn.classList.toggle('on', !!ctl?.auto);
  autoBtn.setAttribute('aria-pressed', String(!!ctl?.auto));
  if (ctl) renderActions(el, ctl);
  if (ctl) placePalette(el, ctl, view);
  // 防衛：攻め手を倒しきったら「次の波へ」（波が来る・攻撃されるまで手番終了を続ける。もう一度押すと止める）
  if (ctl) {
    const guide = el.querySelector<HTMLElement>('.b-guide')!;
    let ws = guide.querySelector<HTMLButtonElement>('.wave-skip');
    const show = ctl.skippingWave || ctl.canSkipWave();
    if (show && !ws) {
      ws = document.createElement('button');
      ws.className = 'btn small wave-skip';
      ws.dataset.act = 'skipwave';
      guide.appendChild(ws);
    }
    if (ws) {
      ws.hidden = !show;
      ws.classList.toggle('on', ctl.skippingWave);
      setHtml(ws, ctl.skippingWave ? `${icon('close')}<span>止める</span>` : `${icon('speed')}<span>次の波へ</span>`);
    }
  }
  // 情報カードを開いたまま攻撃⇔術を切り替えたら、カードの予測も追従させる
  const card = el.querySelector<HTMLElement>('.info-card');
  if (ctl && card && !card.hidden && view.hl.info != null) showInfo(el, board, view.hl.info, view, ctl);
}

/**
 * 六角のアクションパレット（賽3つの戦い・rules 2）：下のボタン列の代わりに、押したマス（無ければ自分）の上へ
 * 賽（移動・その場）を中心に、周りの6マスへ 建築・攻撃・術・道具・手番終了・閉じる を蜂の巣に並べる。
 * 相手・マスを選ぶ間（攻撃・術・建築）は、選んだ所に「実行」と「やめる」だけ。ボタンは下の列と同じもの（押した後の処理も同じ）
 */
const pal = { on: false, open: true, anchor: null as number | null, key: '', last: null as { x: number; y: number; key: string } | null, shown: '' };
/** 周りの6マス（とがった上の六角）：横は hw、縦は 1.5s 単位 */
const PAL_AT: Record<string, [number, number]> = { c: [0, 0], nw: [-0.5, -1], ne: [0.5, -1], e: [1, 0], se: [0.5, 1], sw: [-0.5, 1], w: [-1, 0] };
function placePalette(el: HTMLElement, ctl: BattleController, view: BoardView) {
  if (!pal.on) return;
  const root = el.querySelector<HTMLElement>('.battle')!;
  const box = el.querySelector<HTMLElement>('.actions')!;
  if (box.parentElement !== root) root.appendChild(box);
  box.classList.add('palette');
  const ui = ctl.ui;
  const heroCell = ctl.board.units.find((u) => u.id === ctl.board.current)?.cell ?? null;
  // 手番・段（賽の前・移動の前・移動の後）が変わったら、自分の上に開き直す
  const key = `${ctl.board.turn}:${ctl.board.current}:${ui?.phase ?? ''}`;
  if (key !== pal.key) {
    pal.key = key;
    pal.anchor = null;
    pal.open = true;
  }
  const btns = [...box.querySelectorAll<HTMLElement>(':scope > .act')];
  if (!ui || ctl.auto || ctl.over || heroCell == null || btns.length < 6) {
    box.hidden = true;
    pal.shown = '';
    return;
  }
  const [first, build, attack, skill, item, end] = btns;
  let close = box.querySelector<HTMLElement>('[data-act="palclose"]');
  if (!close) {
    close = document.createElement('button');
    close.className = 'act pal-close';
    close.dataset.act = 'palclose';
    close.innerHTML = `${icon('close')}<span>閉じる</span>`;
    box.appendChild(close);
  }
  const aiming = !!ui.build || !!ui.mode;
  const place: [HTMLElement, string][] = [];
  let anchor = pal.anchor ?? heroCell;
  /** 盤の右下（案内の1行の上）に置く：選ぶマスを隠さないように */
  let corner = false;
  /** 戦いの見込みの小窓（相手を選んだとき） */
  let sim = '';
  // 賽を振った後は、移動が終わるまで開かない（行き先を押す・もう一度押すと移動）。右下に「その場／移動」だけ
  if (ui.phase === 'rolled' && !aiming) {
    corner = true;
    place.push([first, 'c']);
  } else if (!pal.open && !aiming) {
    box.hidden = true;
    pal.shown = '';
    return;
  } else {
    // 相手・マスを選んでいる間も同じパレットのまま（攻撃・術を押し替えて見込みを比べられる。選んだボタンが「実行」、手番終了の所が「やめる」）
    place.push([first, 'c'], [build, 'nw'], [attack, 'ne'], [skill, 'e'], [item, 'se'], [end, 'sw'], [close, 'w']);
    if (ui.target) sim = simHtml(ctl, ui.target);
  }
  // 位置：押したマスの中心。端では周りのボタンが出るよう内側へ寄せる（盤の外の上下の帯にもかからない）
  const k = Math.max(1, 58 / Math.max(1, view.hw));
  const dx = view.hw * k;
  const dy = view.s * 1.5 * k;
  const cv = view.cv;
  const p = view.center(anchor);
  const W = root.clientWidth;
  const top = el.querySelector<HTMLElement>('.b-top')?.getBoundingClientRect().bottom ?? 0;
  const guide = el.querySelector<HTMLElement>('.b-bottom')?.getBoundingClientRect().top ?? root.getBoundingClientRect().bottom;
  const R = root.getBoundingClientRect();
  const lo = { x: dx * 1.5, y: top - R.top + dy * 1.4 };
  const hi = { x: W - dx * 1.5, y: guide - R.top - dy * 1.4 };
  const clamp = (q: { x: number; y: number }) => ({ x: Math.min(hi.x, Math.max(lo.x, q.x)), y: Math.min(hi.y, Math.max(lo.y, q.y)) });
  let at = { x: cv.offsetLeft + p.x, y: cv.offsetTop + p.y };
  // 自分のマスで開くとき・相手やマスを選ぶ間は、自分と選べる所（攻撃の相手・建てられるマス・術のマス）を隠さない所へ。
  // 盤の上を細かく試し、隠すマスが最も少なく、自分に近い所。同じくらいなら前の位置のまま（選ぶたびに跳ねない）
  if (anchor === heroCell || aiming || ui.target) {
    const hl = view.hl;
    const cellPt = (c: number) => {
      const q = view.center(c);
      return { x: cv.offsetLeft + q.x, y: cv.offsetTop + q.y };
    };
    const keep: { x: number; y: number; w: number }[] = [];
    const add = (c: number | null | undefined, w: number) => c != null && c >= 0 && keep.push({ ...cellPt(c), w });
    add(heroCell, 3);
    for (const t of hl.targets) add(t.cell, 2);
    for (const c of hl.build ?? []) add(c, 1);
    for (const c of hl.castCells ?? []) add(c, 1);
    const offs = place.map(([, k]) => PAL_AT[k]);
    const rr = (dx * 0.5 + view.hw * 0.42) ** 2;
    const cost = (q: { x: number; y: number }) => {
      let n = 0;
      for (const c of keep) if (offs.some(([ox, oy]) => (c.x - q.x - ox * dx) ** 2 + (c.y - q.y - oy * dy) ** 2 < rr)) n += c.w;
      return n;
    };
    const hero = cellPt(heroCell);
    const sig = `${key}:${ui.mode ?? ''}:${ui.build ?? ''}`;
    let best = clamp({ x: hero.x, y: hero.y - 2.8 * dy });
    let bestScore = Infinity;
    for (let y = lo.y; y <= hi.y + 0.1; y += dy * 0.5)
      for (let x = lo.x; x <= hi.x + 0.1; x += dx * 0.5) {
        const q = { x, y };
        const sc = cost(q) * 1e6 + Math.hypot(q.x - hero.x, (q.y - hero.y) * 0.8);
        if (sc < bestScore) {
          bestScore = sc;
          best = q;
        }
      }
    const prev = pal.last && pal.last.key.startsWith(`${key}:`) ? clamp(pal.last) : null;
    at = prev && cost(prev) <= cost(best) ? prev : best;
    pal.last = { ...at, key: sig };
  }
  const { x, y } = corner ? { x: W - dx * 0.8, y: guide - R.top - dy * 0.8 } : clamp(at);
  // 開くときだけ広がる動き。描き直しでボタンが作り直されても、同じ所で開いたままなら動かさない（続けて2回出て震えて見えた）
  const shown = `${key}:${place.length}:${Math.round(x)},${Math.round(y)}`;
  box.classList.toggle('pal-still', shown === pal.shown);
  pal.shown = shown;
  box.style.setProperty('--pw', `${dx * 0.96}px`);
  box.style.left = `${x}px`;
  box.style.top = `${y}px`;
  // 見込みの小窓：「実行」「やめる」の上（上に入らなければ相手の下）
  let simEl = box.querySelector<HTMLElement>('.pal-sim');
  if (!simEl) {
    simEl = document.createElement('div');
    simEl.className = 'pal-sim';
    box.appendChild(simEl);
  }
  simEl.hidden = !sim;
  if (sim) {
    simEl.innerHTML = sim;
    const h = simEl.offsetHeight || 60;
    const above = y - dy - dx * 0.58 - h - 6;
    const below = top - R.top + 4 > above;
    simEl.style.setProperty('--sy', below ? `${dy * 0.9}px` : `${-dy - dx * 0.58 - h - 6}px`);
  }
  // 賽をもう振れない（移動の後）：真ん中は空の六角（賽の絵と文字を消す）
  first.classList.toggle('blank', first.dataset.cmd === 'roll' && (first as HTMLButtonElement).disabled);
  for (const b of [...btns, close]) b.hidden = true;
  for (const [b, at] of place) {
    const [ax, ay] = PAL_AT[at];
    b.hidden = false;
    b.dataset.pal = at;
    b.style.setProperty('--px', `${ax * dx}px`);
    b.style.setProperty('--py', `${ay * dy}px`);
  }
  box.hidden = false;
}

/**
 * 戦いの見込み（相手を選んだとき）：文字は出さず、顔と HP のゲージで。
 * 顔の枠は盤の狙いの輪と同じ色（狙った相手は金・範囲で巻き込む相手は赤で小さく・反撃は自分の顔に青と返りの矢印）。減る分は点滅、倒せるゲージは金に光る
 */
function simHtml(ctl: BattleController, target: TargetRef | null | undefined): string {
  const f = ctl.ui?.forecast;
  if (!f || !target) return '';
  const face = (u: VUnit | undefined, core?: 'blue' | 'red') =>
    core
      ? `<span class="ps-face" style="${spriteStyle(core === 'red' ? WORLD.coreRed : WORLD.coreBlue)}"></span>`
      : u?.charId
      ? `<span class="ps-face" style="${faceStyle(u.charId)}"></span>`
      : u
      ? `<span class="ps-face">${unitIcon(u, 26)}</span>`
      : '<span class="ps-face"></span>';
  const bar = (hp: number, after: number, mx: number, side: string) => {
    const b = Math.max(0, Math.min(1, hp / mx));
    const a = Math.max(0, Math.min(b, after / mx));
    return `<span class="ps-bar ${side}${after <= 0 ? ' kill' : ''}"><i class="ps-fill" style="width:${a * 100}%"></i><i class="ps-lose" style="left:${a * 100}%;width:${(b - a) * 100}%"></i></span>`;
  };
  const unit = target.kind === 'actor' ? ctl.board.units.find((u) => u.id === target.id) : undefined;
  const max = target.kind === 'core' ? ctl.board.cores[target.side].max : unit?.max ?? Math.max(1, f.hpBefore);
  const foeSide = target.kind === 'core' ? target.side : unit?.side ?? 'red';
  const rows = [`<p class="ps-row main">${face(unit, target.kind === 'core' ? target.side : undefined)}${bar(f.hpBefore, f.hpAfter, max, foeSide)}</p>`];
  const splash = f.splash.map((s) => ({ s, u: ctl.board.units.find((x) => x.id === s.id) })).filter((x) => x.u);
  for (const { s, u } of splash.slice(0, 3)) rows.push(`<p class="ps-row sub">${face(u)}${bar(u!.hp, Math.max(0, u!.hp - s.dmg), u!.max, u!.side)}</p>`);
  const hero = ctl.board.units.find((u) => u.id === ctl.board.current);
  // 反撃：この攻撃にすぐ返ってくるときだけ（相手の次の手番の攻撃は出さない）
  if (f.riposte > 0 && hero) rows.push(`<p class="ps-row self">${face(hero)}${bar(hero.hp, Math.max(0, hero.hp - f.riposte), hero.max, 'blue')}</p>`);
  return rows.join('');
}

/** 行動ボタンは1行・いつも同じ並び（賽・建築・攻撃・術・道具＋手番終了）。使えないものは淡く。
 *  1つ目は文脈で変わる：賽 → 賽を振った後は「移動」（経路を選ぶ前は「その場」） */
function renderActions(el: HTMLElement, ctl: BattleController) {
  const box = el.querySelector<HTMLElement>('.actions')!;
  const hint = el.querySelector<HTMLElement>('.b-guide .hint')!;
  const stepEl = hint.querySelector<HTMLElement>('.step')!;
  const txt = hint.querySelector<HTMLElement>('.txt')!;
  const legend = el.querySelector<HTMLElement>('.b-guide .legend')!;
  const ui = ctl.ui;
  const L = ui?.legal;
  const setStep = (cur: number) => {
    stepEl.hidden = cur < 0;
    stepEl.textContent = cur >= 0 ? STEPS[cur] : '';
  };
  const setLegend = (reach: boolean, build = false) => {
    legend.innerHTML = `${reach ? '<li class="reach"><i></i>移動可能</li>' : ''}${build ? '<li class="build"><i></i>建築可能</li>' : ''}<li class="blue"><i></i>自陣</li><li class="red"><i></i>敵陣</li>`;
  };
  const btn = (cmd: string, label: string, ic: string, ok: boolean, cls = '', extra = '', badge = '') =>
    `<button class="act ${cls}" data-act="cmd" data-cmd="${cmd}" ${ok ? '' : 'disabled'} ${extra}>${icon(ic)}<span>${label}</span>${badge}</button>`;
  const autoRow = (text: string) => {
    const ar = !!(el.closest('.autorun') ?? el.querySelector('.autorun'));
    txt.textContent = ctl.turbo ? text : ctl.moment || `${ctl.board.defense ? `第${ctl.board.defense.total}波まで守り抜けば勝利` : '敵本陣を落とせば勝利'}${ar ? '' : '・いつでも手動に戻せます'}`;
    box.innerHTML = `<div class="auto-state watching ${ctl.activity.side}">${icon('auto')}<span class="watch-copy"><small>${esc(text)}</small><b>${esc(ctl.turbo ? '戦闘を進めています' : ctl.activity.text)}</b></span>${ar ? '' : '<button class="btn small" data-act="auto">手動に戻す</button>'}</div>`;
  };
  if (ctl.over) {
    setStep(-1);
    setLegend(false);
    txt.textContent = '決着';
    box.innerHTML = '';
    return;
  }
  if (ctl.auto) {
    setStep(-1);
    setLegend(false);
    return autoRow(ctl.turbo ? 'オート（節電）' : 'オート');
  }
  if (!ui || !L) {
    setStep(-1);
    setLegend(false);
    txt.textContent = ctl.activity.text;
    box.innerHTML = [
      btn('roll', '賽', 'dice', false),
      btn('buildmenu', '建築', 'build', false),
      btn('attack', '攻撃', 'attack', false),
      btn('skill', '術', 'skill', false),
      btn('itemmenu', '道具', 'potion', false),
      btn('wait', '手番<wbr>終了', 'end', false, 'end'),
    ].join('');
    return;
  }
  const builds = BUILDING_TYPES.filter((t) => L.builds.has(t) && (L.builds.get(t)?.size ?? 0) > 0);
  const buildInfo = BUILDING_TYPES.some((t) => !['phase', 'locked'].includes(L.buildWhy[t] ?? 'phase'));
  const badge = (n: number | string) => `<em class="cnt">${n}</em>`;
  // 1つ目の枠：賽 → 移動の確定／その場
  let first = btn('roll', '賽', 'dice', L.roll, L.roll ? 'primary' : '');
  if (L.dashing) {
    // 一撃離脱：移動と待機だけ
    const b = ctl.board.budget;
    first = btn('move', '移動', 'move', ui.sel != null, ui.sel != null ? 'primary' : '', '', b ? badge(b) : '');
    box.innerHTML = [
      first,
      btn('buildmenu', '建築', 'build', false),
      btn('attack', '攻撃', 'attack', false),
      btn('skill', '術', 'skill', false),
      btn('itemmenu', '道具', 'potion', false),
      ui.sel != null ? btn('cancel', 'やめる', 'close', true, 'end cancel') : btn('wait', '手番<wbr>終了', 'end', L.wait, 'end dash'),
    ].join('');
    legend.innerHTML = ''; // 案内文を1行に収める
    stepEl.hidden = false;
    stepEl.textContent = '離脱';
    txt.textContent = ui.sel != null ? '移動先を確認して「移動」（手番終了でやめる）' : `一撃離脱：あと${b || 2}マス動けます（手番終了でやめる）`;
    return;
  }
  if (ui.phase === 'rolled') {
    const r = badge(`${ctl.board.budget}歩`);
    first = ui.sel != null ? btn('move', '移動', 'move', true, 'primary', '', r) : btn('stay', '動か<wbr>ない', 'move', true, '', '', r);
  }
  const attackOn = ui.mode === 'attack';
  const skillOn = ui.mode === 'skill';
  const ougiOn = ui.mode === 'ougi';
  // 奥義を構えていれば、術の枠が光る奥義ボタンになる
  // 自分を中心に放つ術を持っていれば、術の枠はその術（構えていれば奥義版）
  const cast = ctl.board.cast;
  const castOn = ui.mode === 'cast';
  const castReady = ctl.armed ? L.castOugi : L.cast;
  const castLabel = castOn && (!cast?.aim || ui.castCell != null) ? `${ctl.armed ? '奥義を' : '術を'}<wbr>放つ` : ctl.armed ? '奥義' : esc(cast?.name ?? '術');
  const skillSlot = cast
    ? btn('cast', castLabel, 'skill', castReady, `${ctl.armed ? 'ougi-btn ' : ''}${castOn ? 'on primary' : ''}`, `aria-pressed="${castOn}"`)
    : ctl.armed
    ? btn('ougi', ougiOn && ui.target ? '奥義を<wbr>放つ' : '奥義', 'skill', L.ougis.length > 0, `ougi-btn ${ougiOn ? 'on primary' : ''}`, `aria-pressed="${ougiOn}"`)
    : btn('skill', skillOn && ui.target ? '術を<wbr>放つ' : '術', 'skill', L.skills.length > 0, skillOn ? 'on primary' : '', `aria-pressed="${skillOn}"`);
  const actions = [
    first,
    // 建てられなくても、解放済みの建物があれば押せる（メニューに建てられない理由が出る）
    `<button class="act ${ui.build ? 'on' : ''} ${builds.length ? '' : 'dim'}" data-act="buildmenu" ${builds.length || buildInfo ? '' : 'disabled'} aria-label="建築（陣材${ctl.board.materials}）">${icon('build')}<span>建築</span>${badge(ctl.board.materials)}</button>`,
    btn('attack', attackOn && ui.target ? '攻撃<wbr>する' : '攻撃', 'attack', L.attacks.length > 0, attackOn ? 'on primary' : '', `aria-pressed="${attackOn}"`),
    skillSlot,
    itemBtn(ctl, L),
  ];
  const cancel = ui.build || ui.target || ui.sel != null;
  const end = cancel ? btn('cancel', 'やめる', 'close', true, 'end cancel') : btn('wait', '手番<wbr>終了', 'end', L.wait, 'end');
  box.innerHTML = actions.join('') + end;
  // 手順と案内（1行）
  setLegend(ui.phase === 'rolled', !!ui.build);
  if (ui.build) {
    setStep(2);
    txt.textContent = `${BUILDING_INFO[ui.build].name}を建てるマスを選んでください`;
  } else if (castOn && cast) {
    setStep(3);
    const desc = ctl.armed ? `奥義：${cast.ougiDesc}` : cast.desc;
    txt.textContent = cast.aim && ui.castCell == null ? `${cast.name}：光るマスを選んでください` : `${desc} →「${ctl.armed ? '奥義を放つ' : '術を放つ'}」`;
  } else if (ui.target) {
    setStep(3);
    const f = ui.forecast;
    const name = ui.target.kind === 'core' ? '敵本陣' : ctl.board.units.find(u => u.id === (ui.target?.kind === 'actor' ? ui.target.id : -1))?.name ?? '対象';
    txt.innerHTML = f && pal.on ? `${esc(name)}を狙っています${f.hpAfter <= 0 ? ' <b class="finish-forecast">撃破可能</b>' : ''}${inflictText(f)} →「${ougiOn ? '奥義を放つ' : skillOn ? '術を放つ' : '攻撃する'}」` : f ? `${esc(name)}に <b class="dmg">${f.dmg}</b> ${f.hpAfter <= 0 ? '<b class="finish-forecast">撃破可能</b>' : `（残HP ${f.hpAfter}）`}${inflictText(f)} →「${ougiOn ? '奥義を放つ' : skillOn ? '術を放つ' : '攻撃する'}」${ougiOn ? '（妖力は壱に戻ります）' : ''}` : '対象を選択中。行動ボタンで実行';
  } else if (ctl.armed) {
    setStep(3);
    txt.textContent = L.ougis.length ? `奥義の構え：相手を選んで放つ` : `奥義の構え：届く相手に近づいてください`;
  } else if (ui.phase === 'start') {
    setStep(0);
    txt.textContent = '賽を振って移動します';
  } else if (ui.phase === 'rolled') {
    setStep(1);
    const bonus = ctl.board.budget - (ctl.board.roll ?? 0);
    const rollText = `賽${ctl.board.roll ?? 0}${bonus ? `・移動補正${bonus > 0 ? '+' : ''}${bonus}` : ''}`;
    const here = ctl.board.units.find((u) => u.id === ctl.board.current)?.cell;
    const canGo = [...(ui.legal?.moves ?? [])].some((c) => c !== here);
    txt.textContent = !canGo
      ? '移動できるマスがありません。「動かない」で行動してください'
      : ui.sel != null
        ? pal.on
          ? `もう一度押すと移動（最大${ctl.board.budget}歩）`
          : `この経路で移動 →「移動」（最大${ctl.board.budget}歩）`
        : pal.on
        ? `行き先を押してください（最大${ctl.board.budget}歩・${rollText}）`
        : `最大${ctl.board.budget}歩 移動できます（${rollText}）`;
  } else {
    setStep(builds.length ? 2 : 3);
    // 移動のあと：できることを並べる。攻撃・術が届く敵がいないときは「射程に敵がいない」とはっきり書き、他にできること（建築・薬・帰還）があれば添える
    const reach = L.attacks.length || L.skills.length;
    const opts = [builds.length ? '建築' : '', L.items.length ? '道具' : ''].filter(Boolean).join('・');
    txt.textContent = reach
      ? builds.length
        ? '建築したあとも攻撃・術が使えます／手番終了'
        : '攻撃・術で行動、または手番終了'
      : `攻撃が届く敵はいません。${opts ? `${opts}ができます／` : ''}手番終了で次へ`;
  }
}

/** 道具のボタン：残りの数。使えるものが無くても、持っていれば押して理由を見られる */
function itemBtn(ctl: BattleController, L: { items: string[] }) {
  const items = ctl.board.items;
  const left = items.reduce((s, x) => s + x.count, 0);
  const ready = L.items.length > 0;
  return `<button class="act ${ready ? '' : 'dim'}" data-act="itemmenu" ${left > 0 && ctl.ui && !ctl.board.dashing ? '' : 'disabled'} aria-label="道具（残り${left}）">${icon('potion')}<span>道具</span><em class="cnt">${left}</em></button>`;
}

const ITEM_WHY: Record<string, string> = { used: 'この手番はもう使いました', empty: 'もうありません', full: 'HPが満タンです', moved: '動いた後は使えません', nothing: '治す状態異常がありません', phase: '今は使えません' };

/** 道具の札：持っている道具（残り・効果）。押すと使う（手番は終わらない） */
function openItemMenu(ctl: BattleController) {
  if (!ctl.ui) return;
  const m = openModal(
    `<div class="build-menu item-menu">${ctl.board.items
      .map(
        (it) => `<button class="build-opt" data-it="${it.id}" ${it.block ? 'disabled' : ''}><span class="thumb it-thumb"><span class="it-art" style="${battleItemArt(it.id)}"></span></span>
        <span class="grow"><b>${esc(it.name)}</b><small>${esc(it.desc)}</small>${it.block ? `<em>${ITEM_WHY[it.block] ?? '今は使えません'}</em>` : ''}</span>
        <span class="bcost">残り${it.count}</span></button>`,
      )
      .join('')}</div>
    <p class="muted small">道具は手番を使いません（1手番に1回）。</p>`,
    { title: '道具' },
  );
  m.body.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-it]');
    if (!b || b.disabled) return;
    m.close();
    void ctl.command('item', b.dataset.it);
  });
}

function toggleBuildMenu(el: HTMLElement, ctl: BattleController) {
  const ui = ctl.ui;
  if (!ui) return;
  const count = ctl.api.buildCount(ctl.b); // 建っている数／同時に建てられる数
  const m = openModal(
    // 本編クリア後の建物は、図面を習得するまで並べない（一覧が長くなるので）
    `<div class="build-menu">${BUILDING_TYPES.filter((t) => !LATE_BUILDING_TYPES.includes(t) || ui.legal.buildWhy[t] !== 'locked').map((t) => {
      const cells = ui.legal.builds.get(t);
      const ok = !!cells && cells.size > 0;
      const info = BUILDING_INFO[t];
      const why = ui.legal.buildWhy[t];
      const cost = ctl.api.buildCostOf(ctl.b, t);
      const sp = BUILDING_SPRITES[t];
      return `<button class="build-opt" data-t="${t}" ${ok ? '' : 'disabled'}><span class="thumb bi" style="${sp ? spriteStyle(sp) : ''}"></span>
        <span class="grow"><b>${info.name}</b><small>${info.desc}</small>${ok ? '' : `<em>${BUILD_WHY[why ?? 'cells'] ?? '建てられません'}</em>`}</span>
        <span class="bcost">${icon('wall')}${cost}</span></button>`;
    }).join('')}</div>
    <p class="muted small">建築は行動を使いません。</p>`,
    { title: '建築' },
  );
  // 見出しの右：建っている数／上限、この手番に建てられる残り、所持の陣材（建てるときに見比べられるように）
  const left = Math.max(0, count.turnMax - count.turnUsed);
  m.el
    .querySelector('.modal-head h2')
    ?.insertAdjacentHTML(
      'afterend',
      `<span class="bm-chips"><span class="bm-chip" title="建っている数／建てられる数">設置<b>${count.built}<small>/${count.max}</small></b></span><span class="bm-chip ${left ? '' : 'none'}" title="この手番に建てられる残り">この手番<b>あと${left}回</b></span><span class="bm-mat" title="所持陣材">${icon('wall')}<b>${fmt(ctl.board.materials)}</b></span></span>`,
    );
  m.body.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-t]');
    if (!b || b.disabled) return;
    m.close();
    void ctl.command('build', b.dataset.t);
  });
  void el;
}

function unitIcon(u: VUnit, size: number) {
  if (u.charId) return `<span class="uicon face" style="width:${rem(size)};height:${rem(size)};${faceStyle(u.charId)}"></span>`;
  if (u.kind === 'building' && u.btype && BUILDING_SPRITES[u.btype]) return `<span class="uicon" style="width:${rem(size)};height:${rem(size)};${spriteStyle(u.side === 'red' ? enemyBuildingRef(u.btype)! : BUILDING_SPRITES[u.btype]!)}"></span>`;
  if (u.enemyId && ENEMIES[u.enemyId]) return enemyIconHtml(u.enemyId, size);
  const st = ROLE_STYLE[u.role ?? 'small'];
  return `<span class="enemy-icon ph" style="width:${rem(size)};height:${rem(size)};--fill:${st.fill};--ring:${st.ring}"><b>${esc(u.name.slice(0, 1))}</b></span>`;
}

// ---------- 情報カード ----------
function infoHtml(board: VBoard, cell: number, ctl?: BattleController) {
  const t = board.tiles[cell];
  const visibleUnits = ctl?.view.vm ? [...ctl.view.vm.units.values()] : board.units;
  const u = visibleUnits.find((x) => x.cell === cell && x.alive && !x.hidden);
  const coreSide = (['red', 'blue'] as const).find((s) => board.cores[s].cell === cell && !(s === 'red' && board.defense));
  if (coreSide) {
    const c = board.cores[coreSide];
    return `<header><b>${coreSide === 'red' ? '敵本陣' : '我が本陣'}</b></header><p>HP ${fmt(c.hp)} / ${fmt(c.max)}</p><p class="muted small">陣地を広げると本陣への攻撃が強くなります。</p>`;
  }
  if (u) {
    const e = u.enemyId ? ENEMIES[u.enemyId] : null;
    const role = u.role ? ROLE_STYLE[u.role].label : '';
    const s = u.stats;
    const statRow = (k: string, v: number | undefined) => (v != null && Number.isFinite(v) ? `<span>${k} <b>${v}</b></span>` : '');
    // 予測は「今選んでいる対象＝このマスのユニット」のときだけ。攻撃と術のどちらの予測かも出す
    const tgt = ctl?.ui?.target;
    const isTarget = !!tgt && tgt.kind === 'actor' && tgt.id === u.id;
    const f = isTarget ? (ctl?.ui?.forecast ?? null) : null;
    const fLabel = ctl?.ui?.mode === 'ougi' ? '奥義' : ctl?.ui?.mode === 'skill' ? '術' : '攻撃';
    return `<header>${unitIcon(u, 30)}<div><b>${esc(u.name)}</b><small>${u.side === 'blue' ? '味方' : u.side === 'red' ? '敵' : '中立'}${role ? `・${role}` : ''}${u.lv ? `・Lv${u.lv}` : ''}</small></div></header>
      <div class="hpbar small"><i style="width:${(u.hp / u.max) * 100}%"></i></div><small>HP ${fmt(u.hp)} / ${fmt(u.max)}</small>
      <div class="stat-row">${statRow('攻', s.atk)}${statRow('防', s.def)}${statRow('速', s.agi)}${statRow('射程', s.range)}</div>
      ${u.surge != null && (u.kind === 'hero' || u.kind === 'rival') && board.surge ? surgeMini(board.surge[u.side === 'red' ? 'red' : 'blue']) : ''}
      ${statusChips(u)}
      ${etypeHtml(u)}
      ${u.btype ? `<p class="small">${BUILDING_INFO[u.btype].desc}</p>` : ''}
      ${u.kind === 'building' && u.side === 'blue' && u.cell != null && ctl?.ui && !ctl.auto && !ctl.over && !ctl.board.dashing ? `<button class="btn small danger bld-demolish" data-act="demolish" data-cell="${u.cell}">${icon('close')}この建物を壊す</button>` : ''}
      ${u.kind === 'hero' && u.side === 'blue' ? companionInfo(ctl) : ''}
      ${e ? `<p class="muted small">図鑑 No.${e.no}</p>` : ''}
      ${f && !pal.on ? `<p class="forecast">${fLabel}の予測ダメージ <b>${f.dmg}</b>（残り ${f.hpAfter}）${f.armor ? '（鎧で軽減）' : ''}${inflictText(f)}</p>` : ''}`;
  }
  const names: Record<string, string> = { grass: '草地', block: '岩・木（通れない）', water: '水辺', home: '本陣前（帰還先）', camp: '妖の野営地', boss: 'ボスの陣', core: '本陣' };
  return `<header><b>${t.relay ? '龍脈' : names[t.kind] ?? 'マス'}</b></header>
    <p>${t.owner ? (t.owner === 'blue' ? '自軍の陣地' : '敵の陣地') : '誰のものでもない'}</p>
    ${t.relay ? '<p class="muted small">止まると占領。全て持って勝つと星が1つ。</p>' : ''}`;
}
/** 状態異常の札（字・名前・効果・残り手番） */
function statusChips(u: VUnit) {
  if (!u.status?.length) return '';
  return `<div class="st-chips">${u.status
    .map((x) => {
      const m = STATUS_MARK[x.key];
      const i = statusInfo(x.key);
      return `<span class="st-chip"><i style="background:${m.bg}">${m.ch}</i><b>${esc(i.name)}</b><small>${esc(i.desc)}・残り${x.turns}手番</small></span>`;
    })
    .join('')}</div>`;
}
/** 予測に添える「＋火傷」など */
function inflictText(f: { inflict?: { key: import('@hyakki/core').StatusKey; turns: number; dmg?: number }[] }) {
  return (f.inflict ?? []).map((x) => `＋${statusInfo(x.key).name}${x.dmg ? ` ${x.dmg}` : ''}`).join('');
}
/** 情報カードの自分のヒーロー：加勢の式神（顔・名前・★・技） */
/** 加勢の式神の説明（戦闘中に右上の丸を押したとき） */
function openCompanionInfo(enemyId: string, star: number, level: number) {
  const k = kaseiInfo(enemyId, level, star);
  if (!k) return;
  openModal(
    `<div class="bc-info"><header><span class="bc-art">${shikiArtHtml(enemyId, 72, star)}</span><span><b>${esc(shikiFormName(enemyId, star))}</b><small>${'★'.repeat(star)} 式神Lv${level}</small></span></header>
      <dl><dt>召喚</dt><dd><b>${esc(k.name)}</b><em>${esc(k.when)}</em>${esc(k.desc)}（1戦に${k.uses}回）</dd><dt>加護</dt><dd>${esc(k.boon.desc)}</dd></dl></div>`,
    { title: '式神召喚', className: 'bc-modal' },
  );
}

function companionInfo(ctl?: BattleController) {
  const c = ctl?.save.setup.loadout.companion;
  if (!c || !SHIKIGAMI[c.enemyId]) return '';
  return `<div class="bt-comp"><span class="cp-face">${shikiArtHtml(c.enemyId, 30, c.star)}</span><span><b>式神召喚 ${esc(shikiFormName(c.enemyId, c.star))} ${'★'.repeat(c.star)} 式神Lv${c.level ?? 1}</b><small>${esc(companionKitText(c.enemyId, c.star, c.level ?? 1))}</small></span></div>`;
}
function showInfo(el: HTMLElement, board: VBoard, cell: number, view: BoardView, ctl?: BattleController) {
  const html = infoHtml(board, cell, ctl);
  const card = el.querySelector<HTMLElement>('.info-card')!;
  const slot = el.querySelector<HTMLElement>('.info-slot');
  if (slot) slot.innerHTML = `<h3>詳細</h3>${html}`;
  // スマホ：押したマスと反対側に出す（押した物が隠れないように）
  const p = view.center(cell);
  card.innerHTML = `${html}<button class="icon-btn close" data-act="closeinfo" aria-label="閉じる">×</button>`;
  // 押したマスが下の方なら札は上に（左下の札で押した物が隠れないように）
  card.classList.toggle('at-top', p.y > view.h * 0.62 && p.x < view.w * 0.6);
  card.hidden = false;
  view.hl.info = cell;
}
function hideInfo(el: HTMLElement, view: BoardView) {
  el.querySelector<HTMLElement>('.info-card')!.hidden = true;
  const slot = el.querySelector<HTMLElement>('.info-slot');
  if (slot) slot.innerHTML = '<h3>詳細</h3><p class="muted small">マスを押すと詳細が出ます</p>';
  view.hl.info = null;
}

// ---------- 演出 ----------
const PIPS: Record<number, number[]> = { 1: [4], 2: [0, 8], 3: [0, 4, 8], 4: [0, 2, 6, 8], 5: [0, 2, 4, 6, 8], 6: [0, 2, 3, 5, 6, 8] };
const dieFace = (n: number) => `<span class="pips">${Array.from({ length: 9 }, (_, i) => `<i class="${(PIPS[n] ?? [4]).includes(i) ? 'on' : ''}"></i>`).join('')}</span>`;

/** 賽3つ（rules 2）の役の演出の音 */
const ROLE_SFX: Record<string, SfxKey> = { pinzoro: 'omikujiSSR', zorome: 'omikujiSR', shigoro: 'omikujiSR', jun: 'omikujiR' };

/**
 * 立体の賽（桃鉄のような白い立方体。1だけ大きな赤い丸）を Canvas に描く。CSS の 3D（preserve-3d）は iPhone の Safari で面がずれるため、
 * 回転行列で6面を正射影し、見えている面（最大3つ）を奥から順に平行四辺形として描く。面：上1・下6・前2・後5・右3・左4
 */
type V3 = [number, number, number];
type M3 = number[];
const mmul = (a: M3, b: M3): M3 => [0, 1, 2].flatMap((r) => [0, 1, 2].map((c) => a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c]));
const mv = (m: M3, v: V3): V3 => [m[0] * v[0] + m[1] * v[1] + m[2] * v[2], m[3] * v[0] + m[4] * v[1] + m[5] * v[2], m[6] * v[0] + m[7] * v[1] + m[8] * v[2]];
const DEG = Math.PI / 180;
/** 軸 ax（単位ベクトル）の周りに a ラジアン（画面の座標：x右・y下・z手前） */
function axisRot(ax: V3, a: number): M3 {
  const [x, y, z] = ax;
  const c = Math.cos(a), s = Math.sin(a), t = 1 - c;
  return [t * x * x + c, t * x * y - s * z, t * x * z + s * y, t * x * y + s * z, t * y * y + c, t * y * z - s * x, t * x * z - s * y, t * y * z + s * x, t * z * z + c];
}
const rotX = (d: number) => axisRot([1, 0, 0], d * DEG);
const rotY = (d: number) => axisRot([0, 1, 0], d * DEG);
const rotZ = (d: number) => axisRot([0, 0, 1], d * DEG);
/** 面：目・外向きの法線・面の上の2軸 */
const FACES: { n: number; nor: V3; u: V3; v: V3 }[] = [
  { n: 1, nor: [0, -1, 0], u: [1, 0, 0], v: [0, 0, 1] },
  { n: 6, nor: [0, 1, 0], u: [1, 0, 0], v: [0, 0, -1] },
  { n: 2, nor: [0, 0, 1], u: [1, 0, 0], v: [0, 1, 0] },
  { n: 5, nor: [0, 0, -1], u: [-1, 0, 0], v: [0, 1, 0] },
  { n: 3, nor: [1, 0, 0], u: [0, 0, -1], v: [0, 1, 0] },
  { n: 4, nor: [-1, 0, 0], u: [0, 0, 1], v: [0, 1, 0] },
];
/** 目 n を上に向ける回転 */
const UP: Record<number, M3> = { 1: rotX(0), 6: rotX(180), 2: rotX(90), 5: rotX(-90), 3: rotZ(-90), 4: rotZ(90) };
const PIP_AT: Record<number, [number, number][]> = {
  1: [[0, 0]],
  2: [[-1, -1], [1, 1]],
  3: [[-1, -1], [0, 0], [1, 1]],
  4: [[-1, -1], [1, -1], [-1, 1], [1, 1]],
  5: [[-1, -1], [1, -1], [0, 0], [-1, 1], [1, 1]],
  6: [[-1, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [1, 1]],
};
const LIGHT: V3 = [-0.35, -0.75, 0.55];
/** 賽の色：移動の賽は白に黒の目（1は赤）、術の賽は藍に金の目（1は白） */
const DICE_LOOK = { move: { body: '#fbf8f0', base: '#cfc7b4', pip: '#1c1a18', one: '#d21a10' }, skill: { body: '#2b3f8c', base: '#18245a', pip: '#f2c75a', one: '#ffffff' } };
function drawCube(cv: HTMLCanvasElement, m: M3, glow: boolean, look: (typeof DICE_LOOK)['move']) {
  const tint = look.body;
  const ctx = cv.getContext('2d')!;
  const W = cv.width;
  const S = W * 0.36;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, W, W);
  const faces = FACES.map((f) => ({ f, nor: mv(m, f.nor), u: mv(m, f.u), v: mv(m, f.v) }))
    .filter((x) => x.nor[2] > 0.01)
    .sort((a, b) => a.nor[2] - b.nor[2]);
  const cx = W / 2, cy = W / 2;
  for (const pass of [0, 1]) {
    for (const { f, nor, u, v } of faces) {
      ctx.setTransform(u[0] * S, u[1] * S, v[0] * S, v[1] * S, cx + nor[0] * 0.5 * S, cy + nor[1] * 0.5 * S);
      if (pass === 0) {
        // 下地（角の丸みのすき間を埋める少し暗い面）
        ctx.fillStyle = look.base;
        ctx.fillRect(-0.5, -0.5, 1, 1);
        continue;
      }
      const lit = Math.max(0, nor[0] * LIGHT[0] + nor[1] * LIGHT[1] + nor[2] * LIGHT[2]);
      const k = 0.78 + 0.22 * lit;
      ctx.fillStyle = tint;
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.roundRect(-0.47, -0.47, 0.94, 0.94, 0.16);
      ctx.fill();
      ctx.fillStyle = `rgba(60, 45, 20, ${(1 - k).toFixed(3)})`;
      ctx.fill();
      if (glow) {
        ctx.strokeStyle = 'rgba(255, 200, 80, 0.9)';
        ctx.lineWidth = 0.05;
        ctx.stroke();
      }
      for (const [px, py] of PIP_AT[f.n]) {
        ctx.beginPath();
        ctx.arc(px * 0.26, py * 0.26, f.n === 1 ? 0.2 : 0.085, 0, Math.PI * 2);
        ctx.fillStyle = f.n === 1 ? look.one : look.pip;
        ctx.fill();
      }
    }
  }
}

/** 賽3つを盤の上に放り投げ、転がって跳ねて止める（共通）。止まった賽の要素を返す */
async function rollThree(pop: HTMLElement, dice: number[], quick: boolean, speed: number) {
  const row = pop.querySelector<HTMLElement>('.dice-row')!;
  row.innerHTML = dice.map(() => '<div class="dslot"><i class="dshadow"></i><canvas class="cube"></canvas></div>').join('');
  const slots = [...row.querySelectorAll<HTMLElement>('.dslot')];
  pop.hidden = false;
  pop.classList.toggle('quick', quick);
  const still = state.settings.reduceShake || matchMedia('(prefers-reduced-motion: reduce)').matches;
  const foe = pop.classList.contains('foe');
  const ms = (foe ? 380 : quick ? 520 : 1000) / speed;
  const gap = foe ? 40 : quick ? 58 : 76;
  const glow = pop.classList.contains('role');
  const tint = pop.classList.contains('skill') ? DICE_LOOK.skill : DICE_LOOK.move;
  const dpr = Math.min(3, devicePixelRatio || 1);
  const runs = slots.map((slot, i) => {
    const cv = slot.querySelector<HTMLCanvasElement>('canvas')!;
    const css = gap * 1.25;
    cv.width = cv.height = Math.round(css * dpr);
    cv.style.width = cv.style.height = `${css}px`;
    // 止まる所：横に並べつつ、少しずつずらす（重ならない）。向きもばらばら
    const fx = (i - 1) * gap + (Math.random() - 0.5) * gap * 0.3;
    const fy = (i === 1 ? -0.18 : 0.12) * gap + (Math.random() - 0.5) * gap * 0.25;
    const yaw = 25 + Math.random() * 40 - 20;
    const end = mmul(mmul(rotX(-35), rotY(yaw)), UP[dice[i]] ?? UP[1]);
    slot.style.transform = `translate(${fx}px, ${fy}px)`;
    if (still) return void drawCube(cv, end, glow, tint), Promise.resolve();
    const sx = fx * 0.4 + (Math.random() - 0.5) * 40;
    const lift = gap * 0.9;
    const dur = ms + (i * 70) / speed;
    const pos = slot.animate(
      [
        { transform: `translate(${sx}px, ${gap * 3.2}px) scale(1.5)`, offset: 0 },
        { transform: `translate(${(sx + fx) / 2}px, ${fy - lift}px) scale(1.25)`, offset: 0.35 },
        { transform: `translate(${fx}px, ${fy}px) scale(1)`, offset: 0.6, easing: 'ease-out' },
        { transform: `translate(${fx}px, ${fy - lift * 0.3}px) scale(1.04)`, offset: 0.74, easing: 'ease-in' },
        { transform: `translate(${fx}px, ${fy}px) scale(1)`, offset: 0.86, easing: 'ease-out' },
        { transform: `translate(${fx}px, ${fy - lift * 0.07}px) scale(1)`, offset: 0.93 },
        { transform: `translate(${fx}px, ${fy}px) scale(1)`, offset: 1 },
      ],
      { duration: dur, easing: 'linear' },
    );
    // 回転：止まる向きの手前で、ばらばらの軸の周りに2〜3回転してから止まる
    const ax: V3 = [Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5];
    const len = Math.hypot(...ax) || 1;
    const axis: V3 = [ax[0] / len, ax[1] / len, ax[2] / len];
    const turns = (2 + Math.random()) * Math.PI * 2;
    const t0 = performance.now();
    return new Promise<void>((resolve) => {
      const frame = (now: number) => {
        const t = Math.min(1, (now - t0) / (dur * 0.9));
        const e = 1 - Math.pow(1 - t, 3);
        drawCube(cv, mmul(end, axisRot(axis, turns * (1 - e))), glow && t >= 1, tint);
        if (t < 1 && cv.isConnected) requestAnimationFrame(frame);
        else void pos.finished.catch(() => undefined).then(() => resolve());
      };
      requestAnimationFrame(frame);
    });
  });
  await Promise.all(runs);
  for (const s of slots) s.classList.add('landed');
  return slots;
}

/** 賽3つ（rules 2）：3つの目・合計・移動。役なら光って大きく告げる */
async function showDice3(el: HTMLElement, value: number, three: { dice: number[]; role?: string; perks?: string[]; enemy?: boolean }, quick: boolean, speed: number) {
  const pop = el.querySelector<HTMLElement>('.dice-pop')!;
  const cap = pop.querySelector<HTMLElement>('.dice-cap')!;
  // 敵将の賽は小さく速く（役が出たときだけ名前と効果を短く。激熱は自分の役だけ）
  pop.className = `dice-pop three${pop.classList.contains('watch') ? ' watch' : ''}${three.enemy ? ' foe' : ''}`;
  cap.innerHTML = '';
  pop.setAttribute('aria-label', `賽の目 ${three.dice.join('・')}`);
  await rollThree(pop, three.dice, quick, speed);
  const sum = three.dice.reduce((a, b) => a + b, 0);
  const role = three.role ? BATTLE_CONFIG.dice3.roles[three.role] : null;
  cap.innerHTML = `<span class="dc-sum">合計${sum}<i>→</i>移動<b>${value}</b></span>`;
  if (role) {
    // キュイーン：役の名前が光って飛び出し、効果を添える
    pop.classList.add('role', `role-${three.role}`);
    // 貴重な役（ピンゾロ・ゾロ目）：ゲームの枠が光って点滅し、光の筋が回る（パチンコの激熱のように）
    if (!three.enemy && (three.role === 'pinzoro' || three.role === 'zorome')) void jackpot(el, three.role, quick, speed);
    cap.innerHTML += `<b class="dc-role">${esc(role.name)}！</b>${three.perks?.length ? `<small class="dc-perks">${three.perks.map(esc).join('・')}</small>` : ''}`;
    sfx(ROLE_SFX[three.role!] ?? 'omikujiR', 0.9);
    // 役はオート（quick）でも見えるだけ止める。貴重な役は長く
    const big = !three.enemy && (three.role === 'pinzoro' || three.role === 'zorome');
    await sleep((three.enemy ? 700 : big ? (quick ? 1500 : 2300) : quick ? 950 : 1300) / speed);
  } else {
    if (three.perks?.length) cap.innerHTML += `<small class="dc-perks">${three.perks.map(esc).join('・')}</small>`;
    await sleep((three.enemy ? 200 : quick ? 300 : 650) / speed);
  }
  pop.hidden = true;
}

/** 激熱：枠が虹色に点滅・白い閃光・紙吹雪。役の音に続けて鳴らす */
const view0Reduced = () => state.settings.reduceShake || matchMedia('(prefers-reduced-motion: reduce)').matches;
async function jackpot(el: HTMLElement, role: string, quick: boolean, speed: number) {
  const root = el.querySelector<HTMLElement>('.battle');
  if (!root) return;
  const fx = document.createElement('div');
  fx.className = `jackpot ${role}`;
  fx.setAttribute('aria-hidden', 'true');
  fx.innerHTML = `<i class="jp-frame"></i><i class="jp-flash"></i>`;
  root.appendChild(fx);
  root.classList.add('jp-on');
  sfx('ougi', 0.8);
  setTimeout(() => sfx('omikujiStamp', 0.9), 300 / speed);
  confetti({ reduced: view0Reduced(), speed });
  await sleep((quick ? 1600 : 2600) / speed);
  root.classList.remove('jp-on');
  fx.remove();
}

/** 術の賽（rules 2）：術・奥義を放つときの3つの目と威力の倍率 */
async function showSkillDice(el: HTMLElement, dice: number[], mult: number, zorome: boolean, quick: boolean, speed: number) {
  const pop = el.querySelector<HTMLElement>('.dice-pop')!;
  const cap = pop.querySelector<HTMLElement>('.dice-cap')!;
  pop.className = `dice-pop three skill${pop.classList.contains('watch') ? ' watch' : ''}`;
  cap.innerHTML = '';
  await rollThree(pop, dice, quick, speed);
  cap.innerHTML = `<span class="dc-sum">術の威力<b>×${mult.toFixed(2)}</b></span>${zorome ? '<b class="dc-role">ゾロ目！</b>' : ''}`;
  if (zorome) {
    pop.classList.add('role', 'role-zorome');
    sfx('omikujiSR', 0.9);
  }
  await sleep((zorome ? (quick ? 700 : 1200) : quick ? 280 : 550) / speed);
  pop.hidden = true;
}

async function showDice(el: HTMLElement, value: number, quick: boolean, speed: number) {
  const pop = el.querySelector<HTMLElement>('.dice-pop')!;
  pop.className = `dice-pop${pop.classList.contains('watch') ? ' watch' : ''}`;
  pop.querySelector<HTMLElement>('.dice-row')!.innerHTML = '<div class="die"></div>';
  pop.querySelector<HTMLElement>('.dice-cap')!.innerHTML = '';
  const die = pop.querySelector<HTMLElement>('.die')!;
  pop.hidden = false;
  pop.classList.toggle('quick', quick);
  die.classList.remove('landed');
  const spins = state.settings.reduceShake || quick ? 1 : 5;
  for (let i = 0; i < spins; i++) {
    die.innerHTML = dieFace(1 + Math.floor(Math.random() * 4));
    await sleep(60 / speed);
  }
  die.innerHTML = dieFace(value);
  die.setAttribute('aria-label', `賽の目 ${value}`);
  die.classList.add('landed');
  await sleep((quick ? 250 : 550) / speed);
  pop.hidden = true;
}

/** 奥義のカットインの絵（読めたものだけ使う。読めなければ顔のカットイン） */
const cutinArt = new Map<string, boolean>();
function preloadCutin(cid: string) {
  if (cutinArt.has(cid)) return;
  cutinArt.set(cid, false);
  const img = new Image();
  img.onload = () => cutinArt.set(cid, true);
  img.src = ougiCutinSrc(cid);
}

/** 奥義のカットイン：画面が白く光り、一枚絵の帯が斜めに走り込む。技の名前は筆文字で大きく */
async function ougiCutIn(el: HTMLElement, name: string, skill: string, view: BoardView, cid: FxLook) {
  const c = el.querySelector<HTMLElement>('.ougi-cut')!;
  c.innerHTML = `<div class="oc-flash"></div><div class="oc-band" style="background-image:url(${ougiCutinSrc(cid)})"><i class="oc-lines"></i></div>
    <div class="oc-text"><small>${esc(name)}・奥義</small><b>${esc(skill)}</b></div>`;
  c.style.setProperty('--k', String(1 / view.speed));
  c.hidden = false;
  c.classList.remove('play');
  void c.offsetWidth;
  c.classList.add('play');
  await sleep(1500 / view.speed);
  c.hidden = true;
  c.classList.remove('play');
}

async function cutIn(el: HTMLElement, name: string, skill: string, side: string, view: BoardView, cid?: FxLook, ougi = false, enemyId?: string) {
  if (skipFx(view)) return;
  if (ougi && cid && cutinArt.get(cid) && !view.reduced) return ougiCutIn(el, name, skill, view, cid);
  const c = el.querySelector<HTMLElement>('.cutin')!;
  const hero = cid && cid !== 'shuten' ? CHARS[cid] : null;
  c.className = `cutin ${side}${ougi ? ' ougi' : ''}`;
  // 敵の大将は盤上のちび絵（leaders）の顔を出す
  const face = hero ? heroStyle(hero.id) : enemyId ? leaderFaceStyle(enemyId) : '';
  c.innerHTML = `${face ? `<span class="ci-face${hero ? '' : ' leader'}" style="${face}"></span>` : ''}<div class="ci-text"><small>${esc(name)}${ougi ? '・奥義' : ''}</small><b>${esc(skill)}</b></div>`;
  // 倍速でも帯が入りきって名前が読める長さは残す（アニメーションも同じ長さに縮める）
  const ms = Math.max(ougi ? 700 : 460, (ougi ? 1300 : 700) / view.speed);
  c.style.setProperty('--dur', `${ms}ms`);
  c.hidden = false;
  await sleep(ms);
  c.hidden = true;
}

/** 奥義を構えた：メダリオンから光が走り、短い告知 */
function ougiArmFx(el: HTMLElement, name: string, view: BoardView) {
  surgeNote(el, `奥義「${name}」を構えた\n「奥義」ボタンで放てます`, 'blue ougi', view.speed);
}

/** 長押しの時間（メダリオン → 奥義の構え） */
const LONG_PRESS_MS = 450;
/** メダリオンを押している最中（HUD を描き直してもリングの溜めを続ける） */
let medalPressing = false;
/** メダリオンの状態：armed＝構え中 / ready＝長押しで構えられる / ''＝どちらでもない */
function ougiState(board: VBoard, ctl?: BattleController | null): '' | 'ready' | 'armed' {
  if (!ctl || !board.ougi || ctl.auto || ctl.over) return '';
  if (ctl.armed) return 'armed';
  return board.ougi.ready && ctl.ui ? 'ready' : '';
}

/** 敵の型の札（重装の鎧の残り・攻城の狙い） */
function etypeHtml(u: VUnit) {
  const t = u.etype;
  if (!t) return '';
  const extra = t.type === 'heavy' ? `鎧 ${t.armor ?? 0}/${t.armorMax ?? 0}` : t.siege ? (t.siege === 'blue' ? '我が本陣を狙っている' : '敵本陣を狙っている') : '';
  return `<div class="etype ${t.type}${t.siege === 'blue' ? ' danger' : ''}"><b>${esc(t.name)}</b>${extra ? `<em>${esc(extra)}</em>` : ''}<small>${esc(t.desc)}</small></div>`;
}

/** 敵の魂：撃破した所から、見えている妖力のゲージへ弧を描いて吸い込まれる光の玉（3つ、少しずつずらす）。届くとゲージが光る */
function soulFly(el: HTMLElement, view: BoardView, cell: number) {
  const gauge = [...el.querySelectorAll<HTMLElement>('.surge-chip, .hc-surge .sorb')].find((g) => g.offsetParent !== null);
  if (!gauge) return;
  const cr = view.cv.getBoundingClientRect();
  const p = view.center(cell);
  const from = { x: cr.left + p.x, y: cr.top + p.y - view.s * 0.35 };
  const gr = gauge.getBoundingClientRect();
  const to = { x: gr.left + gr.width / 2, y: gr.top + gr.height / 2 };
  const glow = () => {
    const chip = [...el.querySelectorAll<HTMLElement>('.surge-chip, .hc-surge .sorb')].find((g) => g.offsetParent !== null);
    if (!chip) return;
    chip.classList.remove('gain');
    void chip.offsetWidth;
    chip.classList.add('gain');
    setTimeout(() => chip.classList.remove('gain'), 600);
  };
  if (view.reduced) return void glow();
  const ms = 750 / Math.max(1, view.speed * 0.8);
  for (let n = 0; n < 3; n++) {
    const o = document.createElement('div');
    o.className = 'soul';
    if (n) o.style.transform = 'scale(0.6)';
    document.body.appendChild(o);
    // 弧：いったん少し浮いてから、横にふくらみながらゲージへ
    const side = (n - 1) * 40;
    const c = { x: (from.x + to.x) / 2 + side - 30, y: Math.min(from.y, to.y) - 40 - n * 10 };
    const frames: Keyframe[] = [];
    for (let i = 0; i <= 12; i++) {
      const t = i / 12;
      const x = (1 - t) * (1 - t) * from.x + 2 * (1 - t) * t * c.x + t * t * to.x;
      const y = (1 - t) * (1 - t) * from.y + 2 * (1 - t) * t * c.y + t * t * to.y;
      const sc = (n ? 0.6 : 1) * (t < 0.15 ? 0.4 + t * 4 : 1 - t * 0.5);
      frames.push({ transform: `translate(${x}px, ${y}px) scale(${sc.toFixed(2)})`, opacity: t > 0.92 ? 0.3 : 1 });
    }
    const a = o.animate(frames, { duration: ms, delay: n * 90, easing: 'cubic-bezier(0.5, 0, 0.75, 0.6)', fill: 'both' });
    a.onfinish = () => {
      o.remove();
      if (n === 0) glow();
    };
  }
}

/** 陣地の段階の変化：画面全体の帯ではなく、綱引きのバーを光らせて小さく知らせる（PC は右の陣地欄） */
/** 文字を読み切れる表示時間（字数に応じて）。倍速でも読める程度にしか縮めない */
function readMs(text: string, speed: number, base = 1300) {
  return Math.min(7000, (base + [...text].length * 85) / Math.sqrt(Math.max(1, speed)));
}

/** 中身が変わったときだけ書き換える（同じ絵を毎回作り直すと、読み直しの間に一瞬消えてちらつく） */
const shownHtml = new WeakMap<Element, string>();
function setHtml(el: Element, html: string) {
  if (shownHtml.get(el) === html) return;
  shownHtml.set(el, html);
  el.innerHTML = html;
}

/** 画面中央の帯。字数に応じて表示し、タップで先へ */
async function banner(el: HTMLElement, text: string, sub: string | undefined, speed: number, fixedMs?: number, kind = '') {
  const b = el.querySelector<HTMLElement>('.b-banner')!;
  const ms = fixedMs ?? readMs(text + (sub ?? ''), speed);
  b.className = `b-banner ${kind}`;
  b.innerHTML = `${kind === 'win' ? '<i class="bb-rays" aria-hidden="true"></i>' : ''}<b>${esc(text)}</b>${sub ? `<small>${esc(sub)}</small>` : ''}`;
  b.style.animationDuration = `${ms}ms`;
  b.hidden = false;
  b.classList.remove('show');
  void b.offsetWidth;
  b.classList.add('show');
  await holdOrTap(b, ms);
  b.hidden = true;
}

/** 防衛戦のルール説明。OK で閉じる。「次回から表示しない」を付けると、以後は名前だけ1秒出して消える */
async function defenseIntro(el: HTMLElement, def: { name: string; desc: string }, speed: number, auto: boolean) {
  if (auto || state.settings.skipDefenseIntro) return banner(el, def.name, undefined, speed, 1000);
  await new Promise<void>((resolve) => {
    const m = openModal(
      `<div class="def-intro">
        <p>${esc(def.desc)}</p>
        <label class="def-skip"><input type="checkbox" data-skip> 次回から表示しない</label>
        <button class="btn primary" data-close>OK</button>
      </div>`,
      {
        title: def.name,
        onClose: () => {
          if (m?.body.querySelector<HTMLInputElement>('[data-skip]')?.checked) setSetting('skipDefenseIntro', true);
          resolve();
        },
      },
    );
  });
}

function pushLog(el: HTMLElement, text: string) {
  const l = el.querySelector('.b-log');
  if (!l) return;
  const li = document.createElement('li');
  li.textContent = text;
  l.prepend(li);
  while (l.children.length > 8) l.lastElementChild?.remove();
}

// ---------- 妖力（戦闘中の成長） ----------
/** 小さな炎3つ（灯った数＝段階、次の炎は進み具合を下から満たす）。色だけでなく数で分かる */
const FLAME_D = 'M6 0C7 4 11 6 11 10.5C11 13.5 8.8 16 6 16S1 13.5 1 10.5C1 6 5 4 6 0Z';
/** 妖力のゲージ：キャラの覚醒のメダリオン（面・金の円環・段階ごとの炎まで描き込んだ絵）。
 *  円環のすぐ外に、次の段階までに必要な撃破数の区切りを細く重ねて、たまった分を灯す。最大は絵の炎だけ（区切りなし）。
 *  メダリオンの無い相手（宿敵など）は段階の字 */
function flamesHtml(sv: SurgeView) {
  const max = sv.toNext == null;
  // 区切りは最大段階までの累計の撃破ポイント（例：弐=2・参=5 なら5つ）。隙間はすべて同じ幅
  const th = sv.thresholds?.length ? sv.thresholds : surgeThresholds();
  const need = th[th.length - 1] ?? 0;
  const lit = max ? need : Math.min(need, Math.max(sv.points, th[sv.stage - 1] ?? 0));
  const R = 12.2;
  const C = 2 * Math.PI * R;
  const unit = need ? C / need : 0;
  const gap = 1.6;
  const arcs = Array.from({ length: need }, (_, i) => {
    return `<circle class="sg${i < lit ? ' on' : ''}" cx="13" cy="13" r="${R}" stroke-dasharray="${(unit - gap).toFixed(2)} ${C.toFixed(2)}" stroke-dashoffset="${(-(i * unit) - gap / 2).toFixed(2)}"/>`;
  }).join('');
  const col = CHAR_IDS.indexOf(sv.key as CharId);
  const y = ((sv.stage - 1) / 2) * 100;
  // 見た目だけ別の人物（プロローグの酒呑童子）は、その人物だけの1列の絵（art/ui/surge-medallion-<look>.webp）
  const art = sv.look
    ? `<i class="md" style="background-image:url(/art/ui/surge-medallion-${esc(sv.look)}.webp);background-size:100% 300%;background-position:0 ${y}%"></i>`
    : col >= 0 ? `<i class="md" style="background-position:${(col / 3) * 100}% ${y}%"></i>` : `<i class="kj">${esc(sv.stageName)}</i>`;
  // 4人：ゲージはメダリオンの絵に彫った溝（art/ui/surge-v2.webp）。次の段階までの進みの分だけ、溝が光で満ちた絵を時計回りに見せる。
  // 弐・参は輪の妖火をコマ送りで重ねる（art/ui/surge-flame-2/3.webp）
  if (col >= 0 && !sv.look) {
    const from = th[sv.stage - 1] ?? 0;
    const to = th[sv.stage];
    const pct = max || to == null ? 100 : Math.max(0, Math.min(100, ((sv.points - from) / Math.max(1, to - from)) * 100));
    const pos = (row: number) => `background-position:${(col / 3) * 100}% ${(row / 5) * 100}%`;
    return `<span class="sorb v2 s${sv.stage}${max ? ' max' : ''}" aria-hidden="true">${sv.stage >= 2 ? `<i class="fl f${sv.stage}"></i>` : ''}<i class="md2" style="${pos(sv.stage - 1)}"></i><i class="md2 full" style="${pos(sv.stage + 2)};--p:${pct.toFixed(1)}%"></i></span>`;
  }
  return `<span class="sorb s${sv.stage}${max ? ' max' : ''}${col >= 0 || sv.look ? '' : ' plain'}" aria-hidden="true">${art}${need ? `<svg viewBox="0 0 26 26">${arcs}</svg>` : ''}</span>`;
}
let surgeTh: number[] | null = null;
function surgeThresholds() {
  if (!surgeTh) {
    try {
      surgeTh = surgeRules().thresholds.slice();
    } catch {
      surgeTh = [0, 2, 5];
    }
  }
  return surgeTh;
}
/** 賽の役・賽の条件の効果（この手番だけ）：妖力のメダリオンの右に、剣（攻撃）・盾（守り）・足（移動）・槌（建築の陣材0）のアイコンで。大きい効果ほど札が大きく光る */
function luckList(board: VBoard): string {
  const me = board.units.find((u) => u.kind === 'hero' && u.side === 'blue');
  const l = me?.luck;
  if (!l) return '';
  const rare = l.role === 'pinzoro' || l.role === 'zorome';
  const chip = (ic: string, lv: number, label: string) => `<span class="luck-chip lv${lv}" title="${esc(label)}" aria-label="${esc(label)}">${icon(ic)}</span>`;
  const atkLv = !l.atk ? 0 : l.atk >= 2 ? 3 : l.atk >= 1.5 ? 2 : 1;
  return `<div class="luck-list${rare ? ' rare' : ''}" aria-label="この手番の賽の効果">${[
    atkLv ? chip('attack', atkLv, `この手番の与ダメ×${l.atk!.toFixed(2)}`) : '',
    l.guard ? chip('shield', 1, `次の手番まで被ダメ×${l.guard.toFixed(2)}`) : '',
    l.move ? chip('foot', 1, `この手番の移動+${l.move}`) : '',
    l.free ? chip('build', 1, 'この手番は建築の陣材0') : '',
  ].join('')}</div>`;
}

function surgeChip(sv: SurgeView, pc = false, og: '' | 'ready' | 'armed' = '') {
  const label = surgeText(sv) + (og === 'ready' ? '・長押しで奥義' : og === 'armed' ? '・奥義の構え（長押しで解除）' : '');
  return `<button class="surge-chip s${sv.stage}${pc ? ' pc' : ''}${og ? ` ${og}` : ''}${medalPressing ? ' pressing' : ''}" data-act="surgeinfo" aria-label="${esc(label)}・詳細" title="${esc(label)}">${flamesHtml(sv)}${og ? '<i class="og-ring" aria-hidden="true"></i>' : ''}${og === 'ready' && !pc ? '<span class="og-hint" aria-hidden="true">長押しで奥義</span>' : ''}</button>`;
}
function surgeMini(sv: SurgeView) {
  return `<div class="surge-mini s${sv.stage}">${flamesHtml(sv)}<b>${esc(surgeText(sv))}</b>${sv.traits.length ? `<small>${sv.traits.map((t) => `${esc(t.name)}：${esc(t.desc)}`).join('<br>')}</small>` : ''}</div>`;
}
function surgeSideHtml(board: VBoard, side: 'blue' | 'red') {
  const sv = board.surge?.[side];
  if (!sv) return '';
  const u = board.units.find((x) => (side === 'blue' ? x.kind === 'hero' : x.kind === 'rival'));
  const name = u?.name ?? (side === 'blue' ? '自分' : '敵ヒーロー');
  const next = sv.stage < sv.maxStage && sv.nextTraits.length ? `<li class="locked"><i>${esc(surgeNameOf(sv, sv.stage + 1))}で解放</i>${sv.nextTraits.map((t) => `<b>${esc(t.name)}</b><span>${esc(t.desc)}</span>`).join('')}</li>` : '';
  return `<section class="sp-side ${side} s${sv.stage}">
    <header>${u ? unitIcon(u, 34) : ''}<div><small>${side === 'blue' ? '自分' : '敵ヒーロー'}</small><b>${esc(name)}</b></div>${flamesHtml(sv)}</header>
    <p class="sp-stage"><b>${esc(surgeText(sv))}</b><small>撃破ポイント ${sv.points}${sv.nextAt != null ? ` / ${sv.nextAt}` : ''}</small></p>
    <ul class="sp-traits">${sv.traits.map((t) => `<li><b>${esc(t.name)}</b><span>${esc(t.desc)}</span></li>`).join('') || '<li class="none">まだ特性は解放されていません</li>'}${next}</ul>
  </section>`;
}
const surgeNameOf = (sv: SurgeView, stage: number) => `${sv.label} ${['壱', '弐', '参'][stage - 1] ?? stage}`;
function surgeRulesHtml(label: string) {
  try {
    const r = surgeRules();
    const pct = (x: number) => `${Math.round(x * 100)}%`;
    const st = r.stages;
    return `<p class="muted small sp-rules">撃破ポイント：中立${r.points.neutral}・敵ヒーロー${r.points.hero}・敵の建物${r.points.building}、番人を倒すと即${r.bossStages}段。累計${r.thresholds[1]}で弐、${r.thresholds[2]}で参。
      弐 攻撃・最大HP+${pct(st[1].atk)}、参 +${pct(st[2].atk)}。上がると最大HPの${pct(st[1].heal)}回復（${esc(label)}は戦闘中だけ）。</p>`;
  } catch {
    return '';
  }
}
/** ≡メニューと詳細の両方で使う：自分と敵ヒーローの妖力・解放済みの特性 */
function surgePanelHtml(board: VBoard, compact = false) {
  if (!board.surge) return '';
  return `<div class="surge-panel${compact ? ' compact' : ''}"><h3 class="sec-title">${esc(board.surge.blue.label)}</h3>${surgeSideHtml(board, 'blue')}${board.units.some((u) => u.kind === 'rival') ? surgeSideHtml(board, 'red') : ''}${compact ? '' : surgeRulesHtml(board.surge.blue.label)}</div>`;
}
/** 陣地の詳細：今の陣地率と段階、地形のルール、各段階の条件と効果（自陣・敵陣のどちらが届いているか） */
function terrDialog(ctl: BattleController) {
  const b = ctl.board;
  const tb = Math.round(b.territory.blue * 100);
  const tr = Math.round(b.territory.red * 100);
  const tiers = b.terrTiers
    .map((t, i) => {
      const mine = b.territory.blue >= t.share;
      const foe = b.territory.red >= t.share;
      return `<li class="${mine ? 'mine' : ''}"><span class="td-ic">${icon(t.icon)}</span><span class="grow"><b>段階${i + 1}<small>陣地率 ${Math.round(t.share * 100)}%以上</small></b><small>${esc(t.text)}</small></span>
        <span class="td-st"><em class="b ${mine ? 'on' : ''}">自陣${mine ? '' : `<i>あと${Math.max(1, Math.ceil(t.share * 100 - tb))}%</i>`}</em><em class="r ${foe ? 'on' : ''}">敵陣</em></span></li>`;
    })
    .join('');
  openModal(
    `<div class="terr-dlg">
      <div class="td-now"><span class="b">自陣<i><b>${tb}</b>%</i><small>第${b.tier.blue}段</small></span><div class="tug-bar big"><i class="b" style="width:${tb}%"></i><i class="r" style="width:${tr}%"></i></div><span class="r">敵陣<i><b>${tr}</b>%</i><small>第${b.tier.red}段</small></span></div>
      ${b.ruleName ? `<p class="td-rule"><b>${esc(b.ruleName)}</b>${esc(b.ruleDesc)}</p>` : ''}
      ${tiers ? `<ol class="td-tiers">${tiers}</ol>` : ''}
      <p class="muted small">歩いたマスが自分の色に塗られ、盤のうち自分の色のマスの割合が陣地率になります。段階の効果は敵にも同じように働きます。</p>
    </div>`,
    { title: '陣地' },
  );
}

function surgeDialog(ctl: BattleController) {
  openModal(surgePanelHtml(ctl.board), { title: `${ctl.board.surge?.blue.label ?? '妖力'}（戦闘中の成長）` });
}

/** 押すか時間で閉じる（スキップ可） */
function holdOrTap(node: HTMLElement, ms: number) {
  return new Promise<void>((resolve) => {
    let done = false;
    const end = () => {
      if (done) return;
      done = true;
      node.removeEventListener('pointerdown', end);
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(end, ms);
    node.addEventListener('pointerdown', end);
  });
}

/** 段階上昇の演出。自分：カットイン（特性の説明まで読める長さ、押すと飛ばす）。敵：上に小さく告知 */
async function surgeFx(el: HTMLElement, ctl: BattleController, e: SurgeEvent, heal: number, u: VUnit | undefined, view: BoardView) {
  const sv = ctl.board.surge?.[e.side];
  const label = sv?.label ?? '妖力';
  const stageName = ['壱', '弐', '参'][e.stage - 1] ?? String(e.stage);
  const traits = e.traits.map((t) => t.name).join('・');
  if (e.side !== 'blue') {
    surgeNote(el, `${u?.name ?? '敵'} ${label} ${stageName}${traits ? `：${traits}` : ''}`, 'red', view.speed);
    await sleep(ctl.auto ? 120 : 260 / view.speed);
    return;
  }
  const skip = skipFx(view);
  if (skip) {
    surgeNote(el, `${label} ${stageName}${traits ? `：${traits}` : ''}${heal ? `（HP+${heal}）` : ''}`, 'blue', view.speed);
    await sleep(150);
    return;
  }
  const c = el.querySelector<HTMLElement>('.surge-cut')!;
  const cid = u?.charId;
  const img = cid ? (e.stage >= 3 ? awakeStyle(cid) : heroStyle(cid)) : '';
  c.className = `surge-cut s${e.stage}`;
  c.innerHTML = `${cid ? `<span class="sc-img" style="${img}"></span>` : ''}<div class="sc-text">
      <small>${esc(u?.name ?? '')}${e.stage >= 3 ? '・覚醒' : ''}</small>
      <b><i>${esc(label)}</i>${esc(stageName)}</b>
      ${e.traits.length ? `<ul>${e.traits.map((t) => `<li><em>${esc(t.name)}</em>${esc(t.desc)}</li>`).join('')}</ul>` : ''}
      ${heal ? `<span class="sc-heal">HP +${heal}</span>` : ''}
    </div>`;
  // 手動：特性の説明まで読める長さ（押すと飛ばす）。オート：観戦の流れを止めないよう短く（説明は戦況の記録に残す）
  const ms = ctl.auto ? 1100 / Math.sqrt(Math.max(1, view.speed)) : readMs(`${label}${stageName}${e.traits.map((t) => t.name + t.desc).join('')}`, view.speed, 1200);
  if (ctl.auto && e.traits.length) pushLog(el, `${label} ${stageName}：${e.traits.map((t) => `${t.name}（${t.desc}）`).join('・')}`);
  c.style.setProperty('--dur', `${ms}ms`);
  c.hidden = false;
  c.classList.remove('show');
  void c.offsetWidth;
  c.classList.add('show');
  await holdOrTap(c, ms);
  c.hidden = true;
}

/** 式神の加勢のカットイン：妖力のカットインの枠を使い、式神の絵（進化の姿）・技の名前・条件・効果。押すと飛ばす。高速・オートは短く */
async function ougiFx(el: HTMLElement, ctl: BattleController, enemyId: string, level: number, u: VUnit | undefined, view: BoardView) {
  const star = ctl.save.setup.loadout.companion?.star ?? 1;
  const k = kaseiInfo(enemyId, level, star);
  const name = shikiFormName(enemyId, star);
  if (!k) return;
  if (u) view.popupUnit(u.id, `${name}・召喚`, 'gold');
  pushLog(el, `式神召喚 ${k.name}（${name}）：${k.desc}`);
  const skip = skipFx(view);
  if (skip) {
    surgeNote(el, `式神召喚 ${name}「${k.name}」：${k.desc}`, 'blue', view.speed);
    await sleep(150);
    return;
  }
  sfx('ougi');
  const c = el.querySelector<HTMLElement>('.surge-cut')!;
  c.className = `surge-cut ougi kasei s${star}`;
  c.innerHTML = `<span class="sc-img sc-shiki">${shikiArtHtml(enemyId, 240, star)}</span><div class="sc-text">
      <small>${esc(name)}・召喚</small>
      <b><i>召喚</i>${esc(k.name)}</b>
      <ul><li><em>${esc(k.when)}</em>${esc(k.desc)}</li></ul>
    </div>`;
  const ms = ctl.auto ? 1000 / Math.sqrt(Math.max(1, view.speed)) : readMs(`${k.name}${k.desc}`, view.speed, 1300);
  c.style.setProperty('--dur', `${ms}ms`);
  c.hidden = false;
  c.classList.remove('show');
  void c.offsetWidth;
  c.classList.add('show');
  view.shake(4);
  await holdOrTap(c, ms);
  c.hidden = true;
}

let noteTimer = 0;
function surgeNote(el: HTMLElement, text: string, cls: string, speed: number) {
  const n = el.querySelector<HTMLElement>('.surge-note');
  if (!n) return;
  n.className = `surge-note ${cls}`;
  n.innerHTML = `<i class="sn-fl" aria-hidden="true"><svg viewBox="0 0 12 16"><path d="${FLAME_D}"/></svg></i><span>${esc(text)}</span>`;
  n.hidden = false;
  n.classList.remove('show');
  void n.offsetWidth;
  n.classList.add('show');
  clearTimeout(noteTimer);
  noteTimer = window.setTimeout(() => (n.hidden = true), readMs(text, speed));
}

const SHIKI_IDS = Object.keys(SHIKIGAMI);
/** 端末のオートの目標 → core の AiOptions（式神候補は meta の SHIKIGAMI で判定） */
function aiOptsOf(g: { shiki: boolean; relays: boolean }) {
  // おすすめビルドの型を選んでいれば、その型の立ち回り（狩り・攻城・決闘など）で動く
  const c = (parse().params.get('char') ?? state.oshi) as CharId;
  const st = styleOf(c, state.profile?.skills?.[c]?.auto ? state.profile.skills[c].style : undefined);
  return { shiki: g.shiki, relays: g.relays, shikiIds: SHIKI_IDS, ...(st ? { style: st.ai, ...(st.aggro ? { aggro: st.aggro } : {}) } : {}) };
}
